// Page Espace Pro - AbracadaWorld
// Drop-in replacement — fidèle à 100% à la maquette HTML validée
// Conserve UnifiedHeader + MainNavigation + EspaceProNav + Footer existants

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import EspaceProSectionsNav, { EspaceProSectionsNavSpacer } from '../../components/layout/EspaceProSectionsNav';
import EspaceProMobileAnchors from '../../components/layout/EspaceProMobileAnchors';
import BeforeAfterSection from '../landing/BeforeAfterSection';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';
import { fadeUpHero, STAGGER, DURATION, EASE } from '../../utils/animations';
import '../../styles/espacepro-mobile.css';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

// ══════════════════════════════════════════════════════════════════════════════
// TOKENS — alignés avec la charte AbracadaWorld
// ══════════════════════════════════════════════════════════════════════════════
const C = {
  navy: '#1E3A5F',
  navyDark: '#162a47',
  navyDeep: '#0f1d33',
  navyMid: '#2557a7',
  orange: '#F97316',
  orangeDeep: '#E8650A',
  orangeHover: '#EA580C',
  orangeCream: '#fce4d4',
  orangePale: '#FFF8F4',
  green: '#22C55E',
  greenDark: '#16a34a',
  red: '#DC2626',
  ink: '#0B1220',
  white: '#FFFFFF',
  gray50: '#F8FAFC',
  gray100: '#F1F5F9',
  gray200: '#E2E8F0',
  gray300: '#CBD5E1',
  gray400: '#94A3B8',
  gray500: '#64748B',
  gray600: '#475569',
  gray700: '#334155',
};

// ══════════════════════════════════════════════════════════════════════════════
// STYLES GLOBAUX (font, tokens, helpers)
// ══════════════════════════════════════════════════════════════════════════════
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

    .ab-root, .ab-root * { box-sizing: border-box; }
    .ab-root {
      font-family: 'DM Sans', system-ui, -apple-system, sans-serif;
      color: ${C.ink};
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }
    .ab-root h1, .ab-root h2, .ab-root h3, .ab-root h4 {
      font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
      letter-spacing: -0.02em;
      line-height: 1.05;
      margin: 0;
    }
    .ab-root .display { font-size: clamp(40px, 6.5vw, 76px); font-weight: 800; }
    .ab-root .hero .display { color: #fff; }
    .ab-root .t-h2 { font-size: clamp(28px, 4.2vw, 48px); font-weight: 800; line-height: 1.08; letter-spacing: -0.02em; }
    .ab-root .t-h3 { font-size: clamp(20px, 2.4vw, 26px); font-weight: 700; }
    .ab-root .t-lead { font-size: clamp(16px, 1.4vw, 19px); color: ${C.gray600}; line-height: 1.6; max-width: 720px; margin: 0 auto; }
    .ab-root .accent { color: ${C.orange}; }
    .ab-root .container { max-width: 1280px; margin: 0 auto; padding: 0 24px; }
    .ab-root .container-narrow { max-width: 1140px; margin: 0 auto; padding: 0 24px; }
    .ab-root .section { padding: 96px 0; }
    .ab-root .section-cream { background: #FFFBF7; }
    @media (max-width: 768px) {
      .ab-root .dual-badge-hide-mobile { display: none; }
      .ab-root .dual-title-hide-mobile { display: none; }
      .ab-root .dual-desc-hide-mobile { display: none; }
      .ab-root .dual-title-mobile-only { display: block !important; }
      .ab-root .prestataires-title-hide-mobile { display: none; }
      .ab-root .hide-on-mobile { display: none !important; }
      .ab-root .show-on-mobile-only { display: block !important; }
    }
    .ab-root .show-on-mobile-only { display: none; }
    @media (max-width: 768px) {
      .ab-root .cockpit-actions-mobile { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
    }
    
    /* Mobile Phone Wrapper - Orange border like a phone screen */
    .ab-root .cockpit-mobile-phone-wrapper {
      background: #fff;
      border: 2.5px solid ${C.orange};
      border-radius: 28px;
      padding: 20px 16px 16px;
      margin: 0 8px;
      box-shadow: 0 8px 32px -8px rgba(249,115,22,0.25), 0 0 0 4px rgba(249,115,22,0.08);
      position: relative;
    }
    .ab-root .cockpit-mobile-phone-wrapper::before {
      content: '';
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: ${C.gray200};
      border-radius: 4px;
    }
    .ab-root .cockpit-mobile-phone-wrapper .cockpit-greeting {
      margin-top: 8px;
    }
    
    /* Claude-style chat input */
    .ab-root .cockpit-chat-claude {
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid ${C.gray100};
    }
    .ab-root .claude-input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      background: ${C.gray50};
      border: 1px solid ${C.gray200};
      border-radius: 24px;
      padding: 6px 6px 6px 16px;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .ab-root .claude-input-wrapper:focus-within {
      border-color: ${C.orange};
      box-shadow: 0 0 0 3px rgba(249,115,22,0.1);
    }
    .ab-root .claude-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      font-size: 14px;
      color: ${C.gray700};
      padding: 6px 0;
    }
    .ab-root .claude-input::placeholder {
      color: ${C.gray400};
    }
    .ab-root .claude-send-btn {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: none;
      background: ${C.orange};
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, transform 0.15s ease;
      flex-shrink: 0;
    }
    .ab-root .claude-send-btn:hover {
      background: ${C.orangeHover};
      transform: scale(1.05);
    }
    
    .ab-root .section-head { text-align: center; margin-bottom: var(--mb, 56px); }
    .ab-root .section-head .t-h2 { margin-top: 16px; }
    .ab-root .section-head .t-lead { margin-top: 18px; }

    /* Eyebrows */
    .ab-root .eyebrow {
      display: inline-flex; align-items: center; gap: 8px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 12px; font-weight: 700;
      letter-spacing: 0.12em; text-transform: uppercase;
      padding: 7px 14px; border-radius: 999px;
    }
    .ab-root .eyebrow .dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
    .ab-root .eyebrow-light { background: rgba(255,255,255,0.12); color: #fff; border: 1px solid rgba(255,255,255,0.22); }
    .ab-root .eyebrow-navy { background: rgba(30,58,95,0.08); color: ${C.navy}; }
    .ab-root .eyebrow-orange { background: rgba(249,115,22,0.10); color: ${C.orangeDeep}; }
    .ab-root .eyebrow-green { background: rgba(34,197,94,0.10); color: ${C.greenDark}; }

    /* Buttons */
    .ab-root .btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px;
      padding: 16px 28px; border-radius: 14px; border: 0; cursor: pointer;
      transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
    }
    .ab-root .btn:hover { transform: translateY(-1px); }
    .ab-root .btn-primary { background: ${C.orange}; color: #fff; box-shadow: 0 10px 24px -8px rgba(249,115,22,0.55); }
    .ab-root .btn-primary:hover { background: ${C.orangeHover}; }
    .ab-root .btn-ghost-light { background: rgba(255,255,255,0.08); color: #fff; border: 1.5px solid rgba(255,255,255,0.28); }
    .ab-root .btn-ghost-light:hover { background: rgba(255,255,255,0.16); }
    .ab-root .btn-navy { background: ${C.navy}; color: #fff; }
    .ab-root .btn-navy:hover { background: ${C.navyDark}; }

    /* Pills */
    .ab-root .pills { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
    .ab-root .pill {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 9px 16px; border-radius: 999px;
      background: rgba(255,255,255,0.08); color: #fff;
      border: 1px solid rgba(255,255,255,0.18);
      font-size: 14px; font-weight: 500;
    }

    /* CTAs row */
    .ab-root .ctas { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; margin-top: 8px; }

    /* Hero base */
    .ab-root .hero {
      position: relative; overflow: hidden; isolation: isolate;
      background: linear-gradient(135deg, ${C.navyDeep} 0%, ${C.navy} 60%, ${C.navyDark} 100%);
      color: #fff;
      padding: 120px 24px 100px;
    }
    .ab-root .hero .inner { position: relative; z-index: 2; max-width: 1200px; margin: 0 auto; }
    .ab-root .hero .orb {
      position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.45; z-index: 0;
    }
    .ab-root .hero .orb-1 { width: 520px; height: 520px; background: ${C.orange}; top: -160px; right: -120px; opacity: 0.25; }
    .ab-root .hero .orb-2 { width: 420px; height: 420px; background: ${C.navyMid}; bottom: -160px; left: -120px; opacity: 0.4; }
    .ab-root .hero-grid-tex {
      position: absolute; inset: 0; z-index: 1; opacity: 0.06;
      background-image:
        linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px);
      background-size: 56px 56px;
    }

    /* Hero V1 — centré */
    .ab-root .hero-v1 { display: block; }
    .ab-root .hero-v1 .badge-row { display: flex; justify-content: center; margin-bottom: 28px; }
    .ab-root .hero-v1 .display { text-align: center; max-width: 980px; margin: 0 auto; }
    .ab-root .hero-v1 .sub-line {
      text-align: center; margin-top: 22px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;
      color: rgba(255,255,255,0.85); font-size: 18px; letter-spacing: 0.02em;
    }
    .ab-root .hero-v1 .lead {
      text-align: center; max-width: 640px; margin: 18px auto 0;
      color: rgba(255,255,255,0.78); font-size: 17px; line-height: 1.6;
    }
    .ab-root .hero-v1 .pills { margin-top: 32px; }
    .ab-root .hero-v1 .ctas { margin-top: 36px; }
    .ab-root .hero-v1 .hero-stats {
      margin-top: 64px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
      padding-top: 40px; border-top: 1px solid rgba(255,255,255,0.12);
    }
    .ab-root .hero-stat { text-align: center; }
    .ab-root .hero-stat .v {
      font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800;
      font-size: clamp(36px, 4vw, 52px); color: ${C.orange}; line-height: 1;
    }
    .ab-root .hero-stat .l {
      margin-top: 8px; font-weight: 600; font-size: 14px;
      letter-spacing: 0.06em; text-transform: uppercase; color: rgba(255,255,255,0.85);
    }
    .ab-root .hero-stat .s { margin-top: 4px; font-size: 13px; color: rgba(255,255,255,0.55); }

    /* Hero V2 — éditorial split */
    .ab-root .hero-v2 { display: block; }
    .ab-root .hero-v2 .inner { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 60px; align-items: center; }
    .ab-root .hero-v2 .left .display { margin-top: 24px; }
    .ab-root .hero-v2 .lead { margin-top: 20px; color: rgba(255,255,255,0.78); font-size: 17px; line-height: 1.65; max-width: 540px; }
    .ab-root .hero-v2 .ctas { justify-content: flex-start; margin-top: 32px; }
    .ab-root .hero-v2 .feature-row { margin-top: 32px; display: flex; flex-direction: column; gap: 14px; }
    .ab-root .hero-v2 .feat { display: flex; align-items: flex-start; gap: 12px; color: rgba(255,255,255,0.88); font-size: 15px; }
    .ab-root .hero-v2 .feat .check {
      flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%;
      background: rgba(34,197,94,0.18); color: #4ade80;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; margin-top: 1px;
    }
    .ab-root .hero-v2 .right { position: relative; height: 480px; }
    .ab-root .hero-v2 .float-card {
      position: absolute; background: rgba(255,255,255,0.08);
      backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
      border: 1px solid rgba(255,255,255,0.18);
      border-radius: 18px; padding: 22px;
      box-shadow: 0 24px 48px -16px rgba(0,0,0,0.35);
    }
    .ab-root .hero-v2 .fc-1 { top: 0; left: 0; width: 280px; }
    .ab-root .hero-v2 .fc-2 { top: 40%; right: 0; width: 290px; transform: translateY(-50%); }
    .ab-root .hero-v2 .fc-3 { bottom: 0; left: 30px; width: 280px; }
    .ab-root .hero-v2 .fc-icon {
      width: 44px; height: 44px; border-radius: 12px;
      background: rgba(249,115,22,0.18); display: flex; align-items: center; justify-content: center;
      font-size: 22px; margin-bottom: 12px;
    }
    .ab-root .hero-v2 .fc-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; margin-bottom: 6px; }
    .ab-root .hero-v2 .fc-desc { font-size: 13px; color: rgba(255,255,255,0.7); line-height: 1.5; }
    @media (max-width: 900px) {
      .ab-root .hero-v2 .inner { grid-template-columns: 1fr; }
      .ab-root .hero-v2 .right { display: none; }
    }

    /* Hero Main — Page Principale Espace Pro avec Centre Commercial */
    .ab-root .hero-main {
      position: relative; overflow: hidden; isolation: isolate;
      background: 
        radial-gradient(ellipse 80% 60% at 65% 40%, rgba(29,78,137,0.5) 0%, transparent 55%),
        radial-gradient(circle at 20% 80%, rgba(255,122,0,0.08) 0%, transparent 40%),
        linear-gradient(165deg, #072B57 0%, #103A6D 35%, #1D4E89 70%, #072B57 100%);
      color: #fff;
      padding: 35px 24px 0;
      min-height: auto;
      display: flex;
      flex-direction: column;
    }
    .ab-root .hero-main .orb-glow {
      position: absolute;
      right: 5%;
      top: 55%;
      transform: translateY(-50%);
      width: 700px;
      height: 700px;
      background: radial-gradient(circle, rgba(249, 115, 22, 0.10) 0%, rgba(249, 115, 22, 0.04) 35%, rgba(249, 115, 22, 0.02) 55%, transparent 75%);
      filter: blur(100px);
      z-index: 0;
      pointer-events: none;
    }
    .ab-root .hero-main .grid-texture {
      position: absolute;
      inset: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E");
      opacity: 0.03;
      z-index: 1;
      pointer-events: none;
    }
    .ab-root .hero-main .stars-texture {
      display: none;
    }
    .ab-root .hero-main .inner {
      position: relative; z-index: 2; max-width: 1400px; margin: 0 auto; flex: 1;
      display: grid; grid-template-columns: 480px 1fr; gap: 40px; align-items: center;
      padding-bottom: 30px;
      width: 100%;
    }
    .ab-root .hero-main .left { 
      max-width: 520px;
      padding-top: 20px;
    }
    .ab-root .hero-main .badge-main {
      display: inline-flex; align-items: center; gap: 10px;
      background: rgba(249, 115, 22, 0.12);
      border: 1px solid rgba(249, 115, 22, 0.25);
      color: #F97316;
      border-radius: 999px;
      padding: 10px 18px;
      font-weight: 600;
      font-size: 12px;
      margin-bottom: 20px;
    }
    .ab-root .hero-main .badge-main::before {
      content: '';
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #F97316;
      box-shadow: 0 0 8px rgba(249, 115, 22, 0.6);
    }
    .ab-root .hero-main .title-main {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 800;
      line-height: 1.1;
      letter-spacing: -0.02em;
      color: #FFFFFF;
      margin: 0 0 8px 0;
    }
    .ab-root .hero-main .title-main .line-big {
      display: block;
      font-size: clamp(36px, 4.5vw, 56px);
      white-space: nowrap;
    }
    .ab-root .hero-main .title-main .line-medium {
      display: block;
      font-size: clamp(28px, 3.2vw, 42px);
      white-space: nowrap;
    }
    .ab-root .hero-main .title-main .orange { color: #F97316; }
    .ab-root .hero-main .impact-orange {
      display: block;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: clamp(18px, 2.2vw, 26px);
      color: #F97316;
      margin-bottom: 16px;
      line-height: 1.3;
      white-space: nowrap;
    }
    .ab-root .hero-main .orange-separator {
      width: 60px;
      height: 4px;
      background: #F97316;
      border-radius: 2px;
      margin-bottom: 20px;
    }
    .ab-root .hero-main .title-sub {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: clamp(16px, 1.8vw, 20px);
      font-weight: 600;
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 16px;
    }
    .ab-root .hero-main .title-sub .orange { color: #F97316; font-weight: 700; }
    .ab-root .hero-main .subtext {
      font-size: clamp(13px, 1.2vw, 14px);
      line-height: 1.65;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 18px;
    }
    .ab-root .hero-main .benefits-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 20px;
    }
    .ab-root .hero-main .benefit-item {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
    }
    .ab-root .hero-main .benefit-check {
      flex-shrink: 0;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: rgba(34, 197, 94, 0.2);
      color: #4ade80;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 10px;
    }
    .ab-root .hero-main .ctas-main {
      display: flex;
      gap: 12px;
      align-items: center;
      flex-wrap: wrap;
      margin-bottom: 16px;
    }
    .ab-root .hero-main .cta-green {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #22C55E;
      color: white;
      border: none;
      border-radius: 12px;
      padding: 12px 20px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 14px;
      font-weight: 700;
      text-decoration: none;
      box-shadow: 0 8px 24px rgba(34,197,94,0.35);
      transition: all 0.2s ease;
      cursor: pointer;
    }
    .ab-root .hero-main .cta-green:hover {
      background: #16A34A;
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(34,197,94,0.42);
    }
    .ab-root .hero-main .cta-orange {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #F97316;
      color: white;
      border: none;
      border-radius: 12px;
      padding: 12px 20px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 14px;
      font-weight: 700;
      text-decoration: none;
      box-shadow: 0 8px 24px rgba(249,115,22,0.35);
      transition: all 0.2s ease;
      cursor: pointer;
    }
    .ab-root .hero-main .cta-orange:hover {
      background: #E8650A;
      transform: translateY(-2px);
    }
    /* Mini badges de réassurance */
    .ab-root .hero-main .mini-badges {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-top: 4px;
    }
    .ab-root .hero-main .mini-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 6px;
      padding: 6px 10px;
      font-size: 11px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.85);
    }
    .ab-root .hero-main .mini-badge svg {
      width: 12px; height: 12px;
    }
    .ab-root .hero-main .mini-badge.green svg { stroke: #4ade80; }
    .ab-root .hero-main .mini-badge.orange svg { stroke: #F97316; }
    .ab-root .hero-main .mini-badge.blue svg { stroke: #60a5fa; }
    
    /* Right Side - Centre Commercial Visual - Image détourée sur fond */
    .ab-root .hero-main .right {
      position: relative;
      min-height: 620px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ab-root .hero-main .building-container {
      position: relative;
      width: 100%;
      height: 100%;
      min-height: 620px;
    }
    .ab-root .hero-main .building-glow {
      position: absolute;
      top: 35%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      height: 60%;
      background: radial-gradient(ellipse 80% 50% at 50% 50%, rgba(249, 115, 22, 0.08) 0%, transparent 60%);
      filter: blur(40px);
      z-index: 0;
      pointer-events: none;
    }
    .ab-root .hero-main .building-image {
      position: absolute;
      top: 45%;
      left: 58%;
      transform: translate(-50%, -50%);
      width: 140%;
      max-width: 900px;
      height: auto;
      z-index: 1;
      filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.25)) drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15));
    }
    
    /* 4 Cards carrées en ligne sous le bâtiment */
    .ab-root .hero-main .cards-row {
      position: absolute;
      bottom: 4%;
      left: 58%;
      transform: translateX(-50%);
      display: flex;
      gap: 12px;
      z-index: 10;
    }
    .ab-root .hero-main .float-card {
      background: #ffffff;
      border-radius: 14px;
      padding: 12px;
      box-shadow: 
        0 4px 6px rgba(0, 0, 0, 0.04),
        0 10px 20px rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      gap: 6px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      width: 100px;
      height: 100px;
    }
    .ab-root .hero-main .float-card:hover {
      transform: translateY(-3px);
      box-shadow: 
        0 6px 12px rgba(0, 0, 0, 0.06),
        0 16px 32px rgba(0, 0, 0, 0.1);
    }
    .ab-root .hero-main .float-card.boutique {
      border-top: 3px solid #F97316;
    }
    .ab-root .hero-main .float-card.cockpit {
      border-top: 3px solid #3b82f6;
    }
    .ab-root .hero-main .float-card.agents {
      border-top: 3px solid #22c55e;
    }
    .ab-root .hero-main .float-card.marketing {
      border-top: 3px solid #a855f7;
    }
    /* Icônes des cards */
    .ab-root .hero-main .fc-icon {
      width: 30px; 
      height: 30px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ab-root .hero-main .fc-icon.orange { 
      background: #FFF7ED; 
      border: 1px solid #FFEDD5;
    }
    .ab-root .hero-main .fc-icon.blue { 
      background: #EFF6FF; 
      border: 1px solid #DBEAFE;
    }
    .ab-root .hero-main .fc-icon.green { 
      background: #F0FDF4; 
      border: 1px solid #DCFCE7;
    }
    .ab-root .hero-main .fc-icon.purple { 
      background: #FAF5FF; 
      border: 1px solid #E9D5FF;
    }
    .ab-root .hero-main .fc-icon svg { width: 16px; height: 16px; }
    .ab-root .hero-main .fc-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: 11px;
      color: #1a2a4a;
      letter-spacing: -0.01em;
    }
    .ab-root .hero-main .fc-desc {
      font-size: 9px;
      color: #1a2a4a;
      line-height: 1.3;
      font-weight: 500;
    }
    .ab-root .hero-main .fc-highlight {
      color: #64748b;
      font-weight: 600;
    }
    
    /* Bottom Banner - Premium avec cadre */
    .ab-root .hero-main .bottom-banner {
      position: relative;
      z-index: 2;
      margin: 0 auto 30px;
      max-width: 1100px;
      width: calc(100% - 48px);
      background: rgba(255, 255, 255, 0.06);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 16px;
      padding: 18px 24px;
    }
    .ab-root .hero-main .banner-grid {
      display: flex;
      justify-content: center;
      gap: 36px;
      flex-wrap: wrap;
    }
    .ab-root .hero-main .banner-item {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
    }
    .ab-root .hero-main .banner-icon {
      width: 34px; height: 34px;
      border-radius: 10px;
      background: rgba(249, 115, 22, 0.15);
      border: 1px solid rgba(249, 115, 22, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .ab-root .hero-main .banner-icon svg {
      width: 16px; height: 16px;
    }
    .ab-root .hero-main .banner-icon.green { 
      background: rgba(34, 197, 94, 0.15); 
      border-color: rgba(34, 197, 94, 0.25);
    }
    .ab-root .hero-main .banner-icon.blue { 
      background: rgba(59, 130, 246, 0.15); 
      border-color: rgba(59, 130, 246, 0.25);
    }
    
    /* Responsive Hero Main */
    @media (max-width: 1200px) {
      .ab-root .hero-main .inner { grid-template-columns: 420px 1fr; gap: 30px; }
      .ab-root .hero-main .building-image { width: 120%; max-width: 750px; }
      .ab-root .hero-main .float-card.boutique { left: 0; }
      .ab-root .hero-main .float-card.agents { right: 0; }
    }
    @media (max-width: 1024px) {
      .ab-root .hero-main .inner { grid-template-columns: 1fr; gap: 50px; }
      .ab-root .hero-main .right { min-height: 500px; }
      .ab-root .hero-main .building-image { width: 100%; max-width: 650px; top: 50%; }
      .ab-root .hero-main .left { max-width: 100%; text-align: center; padding-top: 0; }
      .ab-root .hero-main .ctas-main { justify-content: center; }
      .ab-root .hero-main .benefits-list { align-items: center; }
      .ab-root .hero-main .mini-badges { justify-content: center; }
      .ab-root .hero-main .float-card { min-width: 180px; padding: 12px 16px; }
      .ab-root .hero-main .float-card.boutique { left: 5%; top: 5%; }
      .ab-root .hero-main .float-card.agents { right: 5%; top: 5%; }
      .ab-root .hero-main .float-card.cockpit { bottom: 2%; }
    }
    @media (max-width: 768px) {
      .ab-root .hero-main { 
        padding: 20px 16px 0; 
        min-height: auto; 
      }
      .ab-root .hero-main .inner {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .ab-root .hero-main .left { 
        text-align: center;
        order: 2;
        max-width: 100%;
        padding-top: 0;
      }
      .ab-root .hero-main .right { 
        min-height: auto !important;
        order: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0;
      }
      .ab-root .hero-main .badge-main {
        display: none;
      }
      .ab-root .hero-main .mobile-badge {
        display: none !important;
      }
      .ab-root .hero-main .building-container {
        position: relative;
        display: flex;
        justify-content: center;
        width: 100%;
        min-height: auto !important;
        height: auto !important;
      }
      .ab-root .hero-main .building-glow {
        display: none;
      }
      .ab-root .hero-main .building-image { 
        position: relative !important;
        top: auto !important;
        left: auto !important;
        transform: none !important;
        width: 95% !important; 
        max-width: 360px !important;
        margin: 0 auto;
      }
      .ab-root .hero-main .cards-row { display: none; }
      .ab-root .hero-main .float-card { display: none; }
      .ab-root .hero-main .title-main {
        text-align: center;
        margin-bottom: 8px;
      }
      .ab-root .hero-main .title-main .line-big {
        font-size: 28px;
        white-space: normal;
        line-height: 1.2;
      }
      .ab-root .hero-main .title-main .line-medium {
        font-size: 22px;
        white-space: normal;
        line-height: 1.2;
      }
      .ab-root .hero-main .impact-orange {
        display: block;
        text-align: center;
        font-size: 16px;
        white-space: normal;
        line-height: 1.3;
        margin-bottom: 12px;
      }
      .ab-root .hero-main .orange-separator {
        margin: 12px auto;
        width: 60px;
      }
      .ab-root .hero-main .subtext {
        text-align: center;
        font-size: 14px !important;
        padding: 0 10px;
        margin-bottom: 16px;
      }
      .ab-root .hero-main .ctas-main { 
        flex-direction: row; 
        gap: 12px; 
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 0 16px;
        margin: 0 auto 20px;
      }
      .ab-root .hero-main .cta-green,
      .ab-root .hero-main .cta-orange {
        width: auto;
        justify-content: center;
        padding: 14px 24px;
        font-size: 15px;
      }
      .ab-root .hero-main .mini-badges { 
        gap: 8px; 
        justify-content: center;
        flex-wrap: wrap;
        padding: 0 10px;
      }
      .ab-root .hero-main .mini-badge { 
        padding: 6px 10px; 
        font-size: 11px; 
      }
      .ab-root .hero-main .bottom-banner {
        margin-top: 20px;
      }
    }

    /* Hero V3 — cinéma */
    .ab-root .hero-v3 { display: block; }
    .ab-root .hero-v3 .inner { text-align: center; max-width: 980px; }
    .ab-root .hero-v3 .pre-display {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 9px 18px; border-radius: 999px;
      background: rgba(249,115,22,0.16); color: ${C.orange};
      border: 1px solid rgba(249,115,22,0.35);
      font-weight: 700; font-size: 13px; letter-spacing: 0.04em;
      margin-bottom: 32px;
    }
    .ab-root .hero-v3 .pre-display .icon { font-size: 16px; }
    .ab-root .hero-v3 .display { font-size: clamp(44px, 7vw, 84px); }
    .ab-root .hero-v3 .display .strike {
      color: rgba(255,255,255,0.45); position: relative;
      text-decoration: line-through; text-decoration-color: ${C.red};
      text-decoration-thickness: 4px;
    }
    .ab-root .hero-v3 .lead { margin: 28px auto 0; max-width: 720px; color: rgba(255,255,255,0.78); font-size: 18px; line-height: 1.6; }
    .ab-root .hero-v3 .ctas { margin-top: 36px; }
    .ab-root .hero-v3 .stat-row {
      margin-top: 64px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
    }
    .ab-root .hero-v3 .stat-cell { padding: 28px 16px; border: 1px solid rgba(255,255,255,0.14); border-radius: 16px; background: rgba(255,255,255,0.03); }
    .ab-root .hero-v3 .stat-cell .icon { font-size: 28px; }
    .ab-root .hero-v3 .stat-cell .v { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 44px; color: ${C.orange}; line-height: 1; margin-top: 8px; }
    .ab-root .hero-v3 .stat-cell .l { margin-top: 10px; font-size: 13px; color: rgba(255,255,255,0.7); letter-spacing: 0.04em; }

    /* Eco grid */
    .ab-root .eco-grid {
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-top: 8px;
      position: relative;
    }
    @media (max-width: 1100px) { .ab-root .eco-grid { grid-template-columns: repeat(3, 1fr); } }
    @media (max-width: 760px) { .ab-root .eco-grid { grid-template-columns: 1fr; } }
    .ab-root .eco-card {
      background: #fff; border: 2px solid ${C.orange}; border-radius: 16px;
      padding: 24px; transition: all .2s ease;
    }
    .ab-root .eco-card:hover { transform: translateY(-2px); border-color: ${C.orange}; box-shadow: 0 12px 32px -16px rgba(249,115,22,0.25); background: #FFF8F4; }
    @media (max-width: 760px) {
      .ab-root .eco-card { border: 1px solid ${C.gray200}; }
      .ab-root .eco-card.highlight-mobile { border: 2px solid ${C.orange}; }
    }
    .ab-root .eco-icon { font-size: 32px; margin-bottom: 14px; }
    .ab-root .eco-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; color: ${C.navy}; margin-bottom: 8px; }
    .ab-root .eco-desc { font-size: 14px; color: ${C.gray600}; line-height: 1.55; }
    .ab-root .eco-separator-desktop {
      display: none;
    }
    @media (min-width: 761px) {
      .ab-root .eco-separator-desktop {
        display: flex;
        position: absolute;
        top: 80px;
        left: calc(25% - 22px);
        z-index: 10;
      }
      .ab-root .eco-separator-mobile {
        display: none;
      }
    }
    @media (max-width: 760px) {
      .ab-root .eco-separator-mobile {
        display: flex;
        grid-column: span 1;
      }
    }

    .ab-root .world-banner {
      margin-top: 48px; padding: 36px;
      background: linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%);
      border: 2px solid ${C.orange};
      border-radius: 22px; color: ${C.ink};
      display: grid; grid-template-columns: auto 1fr auto; gap: 24px; align-items: center;
    }
    .ab-root .world-banner .emoji { font-size: 56px; }
    .ab-root .world-banner .t { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700; color: ${C.navy}; }
    .ab-root .world-banner .d { margin-top: 6px; font-size: 15px; color: ${C.gray600}; line-height: 1.55; }
    @media (max-width: 760px) {
      .ab-root .world-banner { grid-template-columns: 1fr; text-align: center; }
      .ab-root .world-banner img { margin: 0 auto; }
      .ab-root .hide-mobile { display: none; }
    }

    /* Assistant */
    .ab-root .assist-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
    @media (max-width: 900px) { .ab-root .assist-grid { grid-template-columns: 1fr; } }
    .ab-root .assist-features { display: flex; flex-direction: column; gap: 16px; }
    .ab-root .assist-feat {
      background: #fff; border: 1px solid ${C.gray200}; border-radius: 16px;
      padding: 22px; display: flex; gap: 16px;
    }
    .ab-root .assist-feat.dark { background: ${C.navy}; color: #fff; border-color: ${C.navy}; }
    .ab-root .assist-feat.orange { background: linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%); border: 2px solid ${C.orange}; }
    .ab-root .assist-feat.orange .ico { background: rgba(249,115,22,0.18); }
    .ab-root .assist-feat.orange .t { color: ${C.orange}; }
    .ab-root .assist-feat.orange .d { color: ${C.gray700}; }
    .ab-root .assist-feat .ico {
      flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px;
      background: rgba(249,115,22,0.12); display: flex; align-items: center; justify-content: center; font-size: 22px;
    }
    .ab-root .assist-feat.dark .ico { background: rgba(249,115,22,0.22); }
    .ab-root .assist-feat .t { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 17px; color: ${C.navy}; }
    .ab-root .assist-feat.dark .t { color: #fff; }
    .ab-root .assist-feat .d { margin-top: 4px; font-size: 14px; color: ${C.gray600}; line-height: 1.5; }
    .ab-root .assist-feat.dark .d { color: rgba(255,255,255,0.72); }

    /* Phone frame + chat */
    .ab-root .phone-frame {
      max-width: 380px; margin: 0 auto;
      background: #0f1d33; border-radius: 36px; padding: 14px;
      box-shadow: 0 30px 60px -20px rgba(15,29,51,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset;
    }
    .ab-root .chat { background: #fff; border-radius: 26px; overflow: hidden; }
    .ab-root .chat-hdr {
      display: flex; align-items: center; gap: 12px;
      background: ${C.navy}; color: #fff; padding: 16px;
    }
    .ab-root .chat-av {
      width: 40px; height: 40px; border-radius: 50%;
      background: ${C.orange}; display: flex; align-items: center; justify-content: center; font-size: 20px;
    }
    .ab-root .chat-hdr-text { flex: 1; }
    .ab-root .chat-hdr-text .name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; }
    .ab-root .chat-hdr-text .sub { font-size: 11px; color: rgba(255,255,255,0.65); margin-top: 2px; }
    .ab-root .chat-online { display: flex; align-items: center; gap: 6px; font-size: 11px; color: rgba(255,255,255,0.85); }
    .ab-root .dot-green { width: 8px; height: 8px; border-radius: 50%; background: ${C.green}; box-shadow: 0 0 8px ${C.green}; }
    .ab-root .chat-body {
      background: ${C.gray50}; padding: 16px; display: flex; flex-direction: column; gap: 12px;
      max-height: 380px; overflow-y: auto;
    }
    .ab-root .msg { display: flex; gap: 8px; align-items: flex-end; }
    .ab-root .msg.user { flex-direction: row-reverse; }
    .ab-root .msg .av {
      width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center; font-size: 14px;
      background: ${C.gray200};
    }
    .ab-root .msg.user .av { background: ${C.navy}; color: #fff; font-size: 12px; font-weight: 700; }
    .ab-root .bubble {
      max-width: 80%; padding: 10px 14px; border-radius: 16px; font-size: 13px; line-height: 1.45;
    }
    .ab-root .bubble.bot { background: #fff; color: ${C.ink}; border: 1px solid ${C.gray200}; border-bottom-left-radius: 4px; }
    .ab-root .bubble.user { background: ${C.navy}; color: #fff; border-bottom-right-radius: 4px; }
    .ab-root .chat-input {
      background: #fff; padding: 12px; border-top: 1px solid ${C.gray200};
      display: flex; align-items: center; gap: 8px;
    }
    .ab-root .chat-btn { width: 32px; height: 32px; border-radius: 50%; border: 0; background: ${C.gray100}; cursor: pointer; }
    .ab-root .chat-input-field { flex: 1; padding: 8px 12px; background: ${C.gray100}; border-radius: 999px; font-size: 13px; color: ${C.gray500}; }
    .ab-root .chat-mic { width: 36px; height: 36px; border-radius: 50%; border: 0; background: ${C.orange}; color: #fff; cursor: pointer; }
    .ab-root .phone-caption { text-align: center; margin-top: 16px; font-size: 13px; color: ${C.gray500}; }

    /* Agents grid */
    .ab-root .agt-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
    @media (max-width: 900px) { .ab-root .agt-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 600px) { .ab-root .agt-grid { grid-template-columns: 1fr; } }
    .ab-root .agt-card {
      background: #fff; border: 1px solid ${C.gray200}; border-radius: 18px; padding: 24px;
      transition: all .2s ease;
    }
    .ab-root .agt-card:hover { transform: translateY(-2px); border-color: ${C.orange}; box-shadow: 0 14px 32px -16px rgba(249,115,22,0.22); }
    .ab-root .agt-emoji { display: inline-block; font-size: 32px; margin-bottom: 12px; }
    .ab-root .agt-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 18px; color: ${C.navy}; }
    .ab-root .agt-role { font-size: 13px; color: ${C.orangeDeep}; font-weight: 600; margin-top: 4px; }
    .ab-root .agt-desc { font-size: 14px; color: ${C.gray600}; line-height: 1.55; margin-top: 10px; }
    .ab-root .magic-banner {
      margin-top: 40px; 
      background: linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%);
      color: ${C.ink};
      border: 2px solid ${C.orange};
      border-radius: 22px; padding: 40px; text-align: center;
    }
    .ab-root .magic-banner .kicker { color: ${C.orange}; font-size: 13px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
    .ab-root .magic-banner .head { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 28px; margin-top: 10px; color: ${C.navy}; }
    .ab-root .magic-banner .body { margin-top: 12px; font-size: 16px; color: ${C.gray600}; max-width: 600px; margin-left: auto; margin-right: auto; line-height: 1.6; }

    /* Cockpit */
    .ab-root .cockpit-shell {
      display: grid; grid-template-columns: 280px 1fr;
      background: #fff; border: 1px solid ${C.gray200}; border-radius: 22px;
      overflow: hidden; box-shadow: 0 24px 60px -24px rgba(15,29,51,0.18);
    }
    @media (max-width: 760px) { .ab-root .cockpit-shell { grid-template-columns: 1fr; } .ab-root .cockpit-side { display: none; } }
    .ab-root .cockpit-side { background: ${C.navy}; color: #fff; padding: 20px; }
    .ab-root .cockpit-side-head { padding-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,0.1); margin-bottom: 16px; }
    .ab-root .cockpit-side-brand { display: flex; align-items: center; gap: 10px; }
    .ab-root .cockpit-side-icon { width: 36px; height: 36px; border-radius: 10px; background: ${C.orange}; display: flex; align-items: center; justify-content: center; font-size: 18px; }
    .ab-root .cockpit-side-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; }
    .ab-root .cockpit-side-sub { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 2px; }
    .ab-root .cockpit-newchat {
      margin-top: 14px; padding: 10px 12px; border-radius: 10px;
      background: ${C.orange}; color: #fff; font-size: 12px; font-weight: 600; text-align: center; cursor: pointer;
    }
    .ab-root .cockpit-menu { display: flex; flex-direction: column; gap: 2px; }
    .ab-root .cockpit-mi {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 12px; border-radius: 8px; cursor: pointer;
      font-size: 13px; color: rgba(255,255,255,0.78);
    }
    .ab-root .cockpit-mi:hover { background: rgba(255,255,255,0.05); }
    .ab-root .cockpit-mi.active { background: rgba(249,115,22,0.18); color: #fff; }
    .ab-root .cockpit-mi.muted { opacity: 0.5; }
    .ab-root .cockpit-mi .ico { font-size: 14px; }
    .ab-root .cockpit-mi .lbl { flex: 1; }
    .ab-root .cockpit-mi .chev { font-size: 10px; opacity: 0.6; }
    .ab-root .cockpit-mi .badge-soon {
      font-size: 9px; font-weight: 700; padding: 3px 6px;
      background: rgba(249,115,22,0.25); color: ${C.orange}; border-radius: 4px; letter-spacing: 0.06em;
    }
    .ab-root .cockpit-submi { padding: 6px 12px 6px 32px; font-size: 12px; color: rgba(255,255,255,0.65); cursor: pointer; }
    .ab-root .cockpit-submi:hover { color: #fff; }
    .ab-root .cockpit-main { padding: 32px; background: #FFFBF7; min-height: 480px; }
    .ab-root .cockpit-greeting { color: ${C.orange}; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; }
    .ab-root .cockpit-hello-block { text-align: center; margin: 24px 0; }
    .ab-root .cockpit-mic {
      display: inline-flex; align-items: center; justify-content: center;
      width: 64px; height: 64px; border-radius: 50%;
      background: linear-gradient(135deg, ${C.orange}, ${C.orangeDeep});
      color: #fff; font-size: 28px; box-shadow: 0 12px 28px -12px rgba(249,115,22,0.6);
    }
    .ab-root .cockpit-hello-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 24px; color: ${C.navy}; margin-top: 14px; }
    .ab-root .cockpit-hello-sub { font-size: 14px; color: ${C.gray500}; margin-top: 6px; }
    .ab-root .cockpit-actions-lbl { font-size: 12px; font-weight: 700; color: ${C.gray500}; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 12px; }
    .ab-root .cockpit-actions { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    @media (max-width: 600px) { .ab-root .cockpit-actions { grid-template-columns: repeat(2, 1fr); } }
    .ab-root .cockpit-action {
      background: #fff; border: 1px solid ${C.gray200}; border-radius: 12px; padding: 12px 8px;
      cursor: pointer; transition: all .15s ease;
      text-align: center;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
    }
    .ab-root .cockpit-action:hover { border-color: ${C.orange}; transform: translateY(-1px); }
    .ab-root .cockpit-action .ico { font-size: 18px; }
    .ab-root .cockpit-action .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 12px; color: ${C.navy}; margin-top: 6px; text-align: center; }
    .ab-root .cockpit-action .sub { font-size: 10px; color: ${C.gray500}; margin-top: 2px; text-align: center; }

    /* Modules */
    .ab-root .module-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 32px; }
    @media (max-width: 900px) { .ab-root .module-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 600px) { .ab-root .module-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; padding: 0 4px; } }
    .ab-root .module {
      background: #fff; border: 1px solid ${C.gray200}; border-radius: 14px; padding: 20px;
    }
    .ab-root .module.muted { opacity: 0.65; }
    .ab-root .module-hdr { display: flex; flex-direction: column; align-items: center; gap: 6px; margin-bottom: 12px; text-align: center; }
    .ab-root .module-hdr + .module-hdr { margin-top: 12px; padding-top: 12px; border-top: 1px solid ${C.gray100}; }
    .ab-root .module-icon { font-size: 24px; }
    .ab-root .module-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 15px; color: ${C.navy}; }
    .ab-root .module-soon { font-size: 9px; font-weight: 700; padding: 3px 6px; background: rgba(249,115,22,0.15); color: ${C.orangeDeep}; border-radius: 4px; letter-spacing: 0.06em; }
    .ab-root .module-list { display: flex; flex-direction: column; gap: 6px; }
    .ab-root .module-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: ${C.gray600}; padding: 4px 0; }
    .ab-root .module-item .arr { color: ${C.orange}; font-weight: 700; }
    @media (max-width: 600px) {
      .ab-root .module { padding: 10px 8px; }
      .ab-root .module-icon { font-size: 20px; }
      .ab-root .module-title { font-size: 11px; }
      .ab-root .module-item { font-size: 10px; gap: 3px; padding: 2px 0; }
      .ab-root .module-list { gap: 3px; }
      .ab-root .module-hdr { gap: 4px; margin-bottom: 8px; }
    }

    .ab-root .cockpit-bottom-banner {
      margin-top: 32px; background: linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%);
      border: 2px solid ${C.orange};
      color: ${C.ink}; border-radius: 20px; padding: 36px;
      display: grid; grid-template-columns: 1fr auto; gap: 32px; align-items: center;
    }
    @media (max-width: 760px) { .ab-root .cockpit-bottom-banner { grid-template-columns: 1fr; } }
    .ab-root .cockpit-bottom-banner .kicker { color: ${C.orange}; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
    .ab-root .cockpit-bottom-banner .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 26px; margin-top: 8px; color: ${C.navy}; }
    .ab-root .cockpit-bottom-banner .desc { margin-top: 8px; color: ${C.gray600}; font-size: 15px; line-height: 1.55; }
    .ab-root .cockpit-bottom-list { display: flex; flex-direction: column; gap: 8px; }
    .ab-root .cockpit-bottom-list .item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: ${C.gray700}; }
    .ab-root .cockpit-bottom-list .ck { color: ${C.green}; font-weight: 700; }

    /* Boutique */
    .ab-root .commission-banner {
      background: #fff; border: 1px solid ${C.gray200}; border-radius: 18px;
      padding: 28px; margin-bottom: 40px;
    }
    .ab-root .commission-title { text-align: center; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; color: ${C.navy}; margin-bottom: 20px; }
    .ab-root .commission-row { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .ab-root .comm-item { text-align: center; padding: 12px 16px; border-radius: 12px; min-width: 110px; }
    .ab-root .comm-item.bad { background: ${C.gray50}; }
    .ab-root .comm-item.good { background: rgba(34,197,94,0.10); border: 1.5px solid ${C.green}; }
    .ab-root .comm-logo { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 13px; color: ${C.gray700}; }
    .ab-root .comm-pct { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 32px; line-height: 1; margin-top: 6px; }
    .ab-root .comm-pct.red { color: ${C.red}; }
    .ab-root .comm-pct.green { color: ${C.green}; }
    .ab-root .comm-label { font-size: 11px; color: ${C.gray500}; margin-top: 4px; }
    .ab-root .comm-label.green { color: ${C.greenDark}; font-weight: 600; }
    .ab-root .comm-vs, .ab-root .comm-arrow { color: ${C.gray400}; font-weight: 700; font-size: 14px; }
    .ab-root .comm-arrow { color: ${C.orange}; font-size: 28px; }
    @media (max-width: 768px) {
      .ab-root .commission-row { gap: 6px; }
      .ab-root .comm-item { min-width: auto; padding: 8px 10px; }
      .ab-root .comm-logo { font-size: 11px; }
      .ab-root .comm-pct { font-size: 22px; }
      .ab-root .comm-label { font-size: 9px; }
      .ab-root .comm-vs { font-size: 10px; }
      .ab-root .comm-arrow { font-size: 20px; }
    }

    .ab-root .boutique-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
    @media (max-width: 900px) { .ab-root .boutique-grid { grid-template-columns: 1fr; } }
    .ab-root .boutique-mockup-label { font-size: 12px; font-weight: 700; color: ${C.gray500}; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 12px; }
    .ab-root .boutique-mockup { background: #fff; border: 1px solid ${C.gray200}; border-radius: 18px; overflow: hidden; box-shadow: 0 18px 40px -20px rgba(15,29,51,0.18); }
    .ab-root .boutique-header { display: flex; align-items: center; gap: 12px; padding: 18px; background: linear-gradient(135deg, #FFF8F4, #fce4d4); border-bottom: 1px solid ${C.gray200}; }
    .ab-root .boutique-avatar { width: 48px; height: 48px; border-radius: 12px; background: ${C.orange}; display: flex; align-items: center; justify-content: center; font-size: 24px; }
    .ab-root .boutique-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 15px; color: ${C.navy}; }
    .ab-root .boutique-tagline { font-size: 12px; color: ${C.gray500}; }
    .ab-root .boutique-verified { background: ${C.green}; color: #fff; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; }
    .ab-root .boutique-body { padding: 18px; }
    .ab-root .boutique-products { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
    .ab-root .prod-card { background: ${C.gray50}; border-radius: 12px; padding: 14px; text-align: center; }
    .ab-root .prod-img { font-size: 32px; margin-bottom: 8px; }
    .ab-root .prod-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600; font-size: 13px; color: ${C.navy}; }
    .ab-root .prod-price { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 18px; color: ${C.orange}; margin-top: 4px; }
    .ab-root .prod-badge { font-size: 10px; color: ${C.greenDark}; margin-top: 6px; }
    .ab-root .via-box { background: ${C.gray50}; border-radius: 14px; padding: 14px; }
    .ab-root .via-hdr { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
    .ab-root .via-av { width: 32px; height: 32px; border-radius: 50%; background: ${C.navy}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px; }
    .ab-root .via-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 13px; color: ${C.navy}; }
    .ab-root .via-sub { font-size: 11px; color: ${C.gray500}; }
    .ab-root .via-dot { width: 8px; height: 8px; border-radius: 50%; background: ${C.green}; box-shadow: 0 0 6px ${C.green}; }
    .ab-root .via-msg { font-size: 12px; padding: 8px 12px; border-radius: 12px; margin-bottom: 6px; max-width: 85%; line-height: 1.4; }
    .ab-root .via-msg.user { background: #fff; border: 1px solid ${C.gray200}; margin-left: auto; }
    .ab-root .via-msg:not(.user) { background: ${C.navy}; color: #fff; }
    .ab-root .via-coins { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 11px; color: ${C.gray600}; }
    .ab-root .via-coin-badge { background: rgba(249,115,22,0.12); color: ${C.orangeDeep}; padding: 3px 8px; border-radius: 999px; font-weight: 700; }
    .ab-root .boutique-feats { display: flex; flex-direction: column; gap: 18px; }
    .ab-root .boutique-feat { background: #fff; border: 1px solid ${C.gray200}; border-radius: 16px; padding: 22px; }
    .ab-root .boutique-feat .ico { font-size: 28px; }
    .ab-root .boutique-feat .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 18px; color: ${C.navy}; margin-top: 10px; }
    .ab-root .boutique-feat .desc { font-size: 14px; color: ${C.gray600}; line-height: 1.55; margin-top: 8px; }

    .ab-root .sequestre {
      margin-top: 40px; background: rgba(34,197,94,0.06); border: 1.5px solid rgba(34,197,94,0.3);
      border-radius: 18px; padding: 28px;
      display: grid; grid-template-columns: auto 1fr; gap: 20px; align-items: start;
    }
    .ab-root .sequestre .ico { font-size: 36px; }
    .ab-root .sequestre .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 20px; color: ${C.greenDark}; }
    .ab-root .sequestre .desc { margin-top: 8px; font-size: 14px; color: ${C.gray700}; line-height: 1.55; }
    .ab-root .sequestre .badges { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
    .ab-root .sequestre .badge { background: #fff; border: 1px solid rgba(34,197,94,0.3); color: ${C.greenDark}; padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; }
    .ab-root .sequestre .badges-mobile { display: none; }
    @media (max-width: 768px) {
      .ab-root .sequestre { grid-template-columns: 1fr; text-align: center; padding: 24px 16px; }
      .ab-root .sequestre .ico { margin: 0 auto; }
      .ab-root .sequestre .ttl { font-size: 16px; white-space: nowrap; }
      .ab-root .sequestre .badges { justify-content: center; }
      .ab-root .sequestre .badges-desktop { display: none !important; }
      .ab-root .sequestre .badges-mobile { display: block !important; }
      .ab-root .sequestre .badge { font-size: 10px; padding: 4px 8px; }
    }

    /* Double porte */
    .ab-root .double-porte {
      margin-top: 56px; background: linear-gradient(135deg, ${C.navy}, ${C.navyDark}); color: #fff;
      border-radius: 24px; padding: 48px;
    }
    .ab-root .dp-badge {
      display: inline-block; background: rgba(249,115,22,0.18); color: ${C.orange};
      padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
    }
    .ab-root .dp-headline { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 32px; margin-top: 16px; line-height: 1.15; }
    .ab-root .dp-desc { margin-top: 14px; color: rgba(255,255,255,0.78); font-size: 16px; line-height: 1.6; max-width: 720px; }
    .ab-root .dp-doors { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 32px; }
    @media (max-width: 760px) { .ab-root .dp-doors { grid-template-columns: 1fr; } }
    .ab-root .dp-door { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.14); border-radius: 18px; padding: 26px; position: relative; }
    .ab-root .dp-door.d2 { background: rgba(249,115,22,0.10); border-color: rgba(249,115,22,0.4); }
    .ab-root .dp-free-tag { position: absolute; top: -12px; right: 18px; background: ${C.orange}; color: #fff; font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 999px; }
    .ab-root .dp-door-icon { font-size: 32px; }
    .ab-root .dp-door-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 18px; margin-top: 12px; }
    .ab-root .dp-door-url { font-family: 'DM Sans', monospace; font-size: 12px; color: ${C.orange}; margin-top: 6px; }
    .ab-root .dp-door-feats { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
    .ab-root .dp-feat-row { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; color: rgba(255,255,255,0.85); line-height: 1.45; }
    .ab-root .dp-feat-row .ck { color: ${C.green}; font-weight: 700; }

    .ab-root .webmaster-bloc { margin-top: 28px; background: rgba(0,0,0,0.18); border-radius: 16px; padding: 22px; border: 1px solid rgba(255,255,255,0.08); }
    .ab-root .wb-hdr { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
    .ab-root .wb-av { width: 36px; height: 36px; border-radius: 50%; background: ${C.orange}; display: flex; align-items: center; justify-content: center; font-size: 18px; }
    .ab-root .wb-info { flex: 1; }
    .ab-root .wb-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; }
    .ab-root .wb-role { font-size: 12px; color: rgba(255,255,255,0.65); margin-top: 2px; }
    .ab-root .wb-status { display: flex; align-items: center; gap: 6px; font-size: 11px; color: rgba(255,255,255,0.85); }
    .ab-root .wb-msgs { display: flex; flex-direction: column; gap: 8px; }
    .ab-root .wb-msg { padding: 10px 14px; border-radius: 14px; font-size: 13px; line-height: 1.5; max-width: 92%; }
    .ab-root .wb-msg.u { background: rgba(255,255,255,0.06); margin-left: auto; }
    .ab-root .wb-msg.a { background: rgba(249,115,22,0.12); border: 1px solid rgba(249,115,22,0.28); }
    .ab-root .wb-msg .done { color: ${C.green}; font-weight: 700; }

    .ab-root .dp-cockpit {
      margin-top: 24px; display: grid; grid-template-columns: auto 1fr auto; gap: 16px; align-items: center;
      background: rgba(249,115,22,0.10); border: 1px solid rgba(249,115,22,0.28); border-radius: 14px; padding: 18px 22px;
    }
    .ab-root .dp-cockpit-icon { font-size: 28px; }
    .ab-root .dp-cockpit-ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; }
    .ab-root .dp-cockpit-sub { font-size: 13px; color: rgba(255,255,255,0.7); margin-top: 2px; }
    .ab-root .dp-cockpit-arrow { color: ${C.orange}; font-size: 24px; font-weight: 700; }

    /* Prestataires */
    .ab-root .prest-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
    @media (max-width: 1000px) { .ab-root .prest-grid { grid-template-columns: 1fr; } }
    .ab-root .search-wrap { background: #fff; border: 2px solid ${C.navy}; border-radius: 18px; padding: 22px; }
    .ab-root .search-bar { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: ${C.gray50}; border-radius: 12px; }
    .ab-root .search-icon { font-size: 18px; }
    .ab-root .search-input { flex: 1; color: ${C.gray500}; font-size: 14px; }
    .ab-root .search-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
    .ab-root .filter-chip { padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; background: ${C.gray100}; color: ${C.gray600}; }
    .ab-root .filter-chip.active { background: ${C.navy}; color: #fff; }
    .ab-root .artisan-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 18px; }
    @media (max-width: 600px) { .ab-root .artisan-cards { grid-template-columns: 1fr; } }
    .ab-root .artisan-card { background: ${C.gray50}; border-radius: 14px; overflow: hidden; }
    .ab-root .artisan-banner { position: relative; padding: 24px; background: linear-gradient(135deg, #FFF8F4, ${C.orangeCream}); display: flex; align-items: center; justify-content: space-between; }
    .ab-root .artisan-avatar { width: 44px; height: 44px; border-radius: 12px; background: ${C.orange}; display: flex; align-items: center; justify-content: center; font-size: 22px; color: #fff; }
    .ab-root .artisan-verified { font-size: 10px; font-weight: 700; padding: 3px 8px; background: ${C.green}; color: #fff; border-radius: 999px; }
    .ab-root .artisan-body { padding: 14px; }
    .ab-root .artisan-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; color: ${C.navy}; }
    .ab-root .artisan-meta { font-size: 11px; color: ${C.gray500}; margin-top: 4px; }
    .ab-root .artisan-stars { font-size: 13px; color: ${C.orange}; font-weight: 700; margin-top: 6px; }
    .ab-root .artisan-stars .count { color: ${C.gray500}; font-weight: 400; font-size: 11px; }
    .ab-root .artisan-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
    .ab-root .artisan-tag { font-size: 10px; padding: 3px 7px; background: rgba(30,58,95,0.08); color: ${C.navy}; border-radius: 999px; }
    .ab-root .artisan-btn { width: 100%; margin-top: 10px; padding: 8px; background: ${C.navy}; color: #fff; border: 0; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; }

    .ab-root .vs-block { margin-top: 24px; display: flex; flex-direction: column; gap: 12px; }
    .ab-root .vs-bad, .ab-root .vs-good { display: grid; grid-template-columns: auto 1fr; gap: 16px; padding: 20px; border-radius: 14px; align-items: start; }
    .ab-root .vs-bad { background: rgba(220,38,38,0.06); border: 1px solid rgba(220,38,38,0.2); }
    .ab-root .vs-good { background: rgba(34,197,94,0.06); border: 1.5px solid rgba(34,197,94,0.3); }
    .ab-root .vs-bad .ico, .ab-root .vs-good .ico { font-size: 28px; }
    .ab-root .vs-bad .ttl, .ab-root .vs-good .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; color: ${C.navy}; }
    .ab-root .vs-bad .desc, .ab-root .vs-good .desc { font-size: 13px; color: ${C.gray600}; line-height: 1.5; margin-top: 6px; }

    /* Devis flow */
    .ab-root .devis-flow { background: #fff; border: 2px solid ${C.orange}; border-radius: 18px; padding: 24px; }
    .ab-root .devis-flow .kicker { color: ${C.orangeDeep}; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 18px; }
    .ab-root .devis-steps { display: flex; flex-direction: column; gap: 16px; }
    .ab-root .devis-step { display: grid; grid-template-columns: auto 1fr; gap: 14px; }
    .ab-root .ds-num { width: 32px; height: 32px; border-radius: 50%; background: ${C.orange}; color: #fff; display: flex; align-items: center; justify-content: center; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 14px; }
    .ab-root .ds-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 15px; color: ${C.navy}; }
    .ab-root .ds-desc { font-size: 13px; color: ${C.gray600}; line-height: 1.5; margin-top: 4px; }
    .ab-root .ds-tag { display: inline-block; margin-top: 8px; padding: 4px 10px; background: rgba(249,115,22,0.10); color: ${C.orangeDeep}; border-radius: 999px; font-size: 11px; font-weight: 600; }
    .ab-root .ia-selection { margin-top: 20px; padding: 18px; background: ${C.gray50}; border-radius: 14px; }
    .ab-root .ia-sel-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 13px; color: ${C.navy}; margin-bottom: 12px; }
    .ab-root .ia-artisans { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
    .ab-root .ia-artisan { background: #fff; border-radius: 10px; padding: 10px; text-align: center; }
    .ab-root .ia-av { font-size: 20px; }
    .ab-root .ia-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 11px; color: ${C.navy}; margin-top: 4px; }
    .ab-root .ia-score { font-size: 10px; color: ${C.gray500}; margin-top: 2px; }
    .ab-root .ia-match { font-size: 10px; color: ${C.green}; font-weight: 700; margin-top: 4px; }

    .ab-root .prest-av-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 48px; }
    @media (max-width: 900px) { .ab-root .prest-av-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 600px) { .ab-root .prest-av-grid { grid-template-columns: 1fr; } }
    .ab-root .prest-av { background: #fff; border: 1px solid ${C.gray200}; border-radius: 14px; padding: 22px; }
    .ab-root .prest-av .ico { font-size: 28px; }
    .ab-root .prest-av .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; color: ${C.navy}; margin-top: 10px; }
    .ab-root .prest-av .desc { font-size: 13px; color: ${C.gray600}; line-height: 1.55; margin-top: 6px; }

    /* Social/SEO */
    .ab-root .social-seo { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 48px; }
    @media (max-width: 900px) { .ab-root .social-seo { grid-template-columns: 1fr; } }
    .ab-root .rs-card { 
      background: #fff;
      color: ${C.ink}; 
      border-radius: 22px; 
      padding: 32px;
      border: 2px solid ${C.navy};
    }
    .ab-root .rs-card.orange { 
      background: #fff;
      border: 2px solid ${C.orange};
    }
    .ab-root .rs-card .kicker { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${C.navy}; }
    .ab-root .rs-card.orange .kicker { color: ${C.orange}; }
    .ab-root .rs-card .head { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 26px; margin-top: 10px; line-height: 1.15; color: ${C.navy}; }
    .ab-root .rs-card.orange .head { color: ${C.orange}; }
    .ab-root .rs-card .desc { margin-top: 14px; font-size: 14px; color: ${C.gray600}; line-height: 1.55; }
    .ab-root .rs-post { background: ${C.gray50}; border: 1px solid ${C.gray200}; border-radius: 14px; padding: 16px; margin-top: 18px; }
    .ab-root .rs-post-hdr { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
    .ab-root .rs-post-av { width: 32px; height: 32px; border-radius: 50%; background: ${C.orange}; display: flex; align-items: center; justify-content: center; font-size: 16px; }
    .ab-root .rs-post-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 13px; color: ${C.ink}; }
    .ab-root .rs-post-pub { font-size: 11px; color: ${C.gray500}; }
    .ab-root .rs-post-cert { font-size: 10px; padding: 3px 8px; background: ${C.green}; color: #fff; border-radius: 999px; font-weight: 700; white-space: nowrap; }
    .ab-root .rs-post-body { font-size: 13px; line-height: 1.5; color: ${C.gray700}; }
    .ab-root .rs-dup-lbl { margin-top: 14px; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; color: ${C.gray500}; }
    .ab-root .rs-net-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .ab-root .rs-net { padding: 5px 10px; background: ${C.gray100}; border-radius: 999px; font-size: 11px; color: ${C.gray700}; }
    .ab-root .cm-bloc { margin-top: 18px; padding: 16px; background: linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%); border-radius: 12px; }
    .ab-root .cm-bloc .lbl { font-size: 12px; font-weight: 700; color: ${C.navy}; margin-bottom: 8px; }
    .ab-root .cm-bloc .row { display: flex; align-items: center; gap: 8px; font-size: 12px; color: ${C.gray700}; padding: 4px 0; }
    .ab-root .cm-bloc .ck { color: ${C.green}; font-weight: 700; }
    .ab-root .seo-page { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; padding: 12px 14px; background: ${C.gray50}; border: 1px solid ${C.gray200}; border-radius: 12px; margin-top: 8px; }
    .ab-root .seo-page-ic { font-size: 18px; }
    .ab-root .seo-page-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 13px; color: ${C.ink}; }
    .ab-root .seo-page-url { font-family: monospace; font-size: 11px; color: ${C.gray500}; margin-top: 2px; }
    .ab-root .seo-page-on { font-size: 10px; padding: 3px 8px; background: ${C.green}; color: #fff; border-radius: 999px; font-weight: 700; }
    .ab-root .seo-summary { margin-top: 14px; padding: 14px; background: rgba(249,115,22,0.08); border-radius: 12px; }
    .ab-root .seo-summary .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; color: ${C.orange}; }
    .ab-root .seo-summary .desc { font-size: 12px; color: ${C.gray700}; line-height: 1.5; margin-top: 6px; }

    /* Setup */
    .ab-root .setup-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    @media (max-width: 900px) { .ab-root .setup-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 600px) { .ab-root .setup-grid { grid-template-columns: 1fr; } }
    .ab-root .setup-card { background: #fff; border: 1px solid ${C.gray200}; border-radius: 16px; padding: 18px; padding-top: 44px; position: relative; margin-top: 18px; }
    @media (max-width: 600px) { .ab-root .setup-card { display: grid; grid-template-columns: 32px 1fr; grid-template-rows: auto auto; gap: 0 12px; align-items: start; padding: 14px 16px; padding-top: 44px; margin-top: 18px; } }
    .ab-root .setup-num { width: 32px; height: 32px; border-radius: 10px; background: rgba(34,197,94,0.12); color: ${C.greenDark}; display: flex; align-items: center; justify-content: center; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 14px; }
    @media (max-width: 600px) { .ab-root .setup-num { grid-row: 1 / 3; align-self: center; } }
    .ab-root .setup-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; color: ${C.navy}; margin-top: 14px; }
    @media (max-width: 600px) { .ab-root .setup-title { margin-top: 0; font-size: 15px; line-height: 1.3; } }
    .ab-root .setup-desc { font-size: 13px; color: ${C.gray600}; line-height: 1.55; margin-top: 8px; }
    @media (max-width: 600px) { .ab-root .setup-desc { margin-top: 4px; font-size: 12px; } }
    .ab-root .setup-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, #FFF8F4 0%, #FFEEE4 100%); color: ${C.orange}; font-size: 9px; font-weight: 700; padding: 3px 10px; border-radius: 4px; text-transform: uppercase; border: 1px solid ${C.orange}; }
    .ab-root .setup-magic {
      grid-column: 1 / -1; background: linear-gradient(135deg, ${C.navy}, ${C.navyDark});
      color: #fff; border-radius: 18px; padding: 28px;
      display: grid; grid-template-columns: auto 1fr; gap: 20px; align-items: center;
    }
    .ab-root .setup-magic .emoji { font-size: 48px; }
    .ab-root .setup-magic .kicker { color: ${C.orange}; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
    .ab-root .setup-magic .ttl { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 22px; margin-top: 6px; }
    .ab-root .setup-magic .desc { margin-top: 8px; font-size: 14px; color: rgba(255,255,255,0.82); line-height: 1.55; }

    /* Comment ça marche */
    .ab-root .comment-grid { display: grid; grid-template-columns: 1fr auto 1fr auto 1fr; gap: 20px; align-items: start; }
    @media (max-width: 900px) { .ab-root .comment-grid { grid-template-columns: 1fr; } .ab-root .comment-arrow { display: none; } }
    .ab-root .comment-step { text-align: center; padding: 24px; }
    .ab-root .comment-circle { width: 96px; height: 96px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 40px; position: relative; margin-bottom: 18px; }
    .ab-root .comment-num { position: absolute; bottom: -6px; right: -6px; width: 28px; height: 28px; border-radius: 50%; background: ${C.orange}; color: #fff; display: flex; align-items: center; justify-content: center; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 12px; border: 3px solid #FFFBF7; }
    .ab-root .comment-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 18px; color: ${C.navy}; }
    .ab-root .comment-desc { margin-top: 8px; font-size: 14px; color: ${C.gray600}; line-height: 1.55; }
    .ab-root .comment-tag { display: inline-block; margin-top: 12px; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; }
    .ab-root .comment-arrow { padding-top: 60px; color: ${C.gray400}; font-size: 28px; }

    .ab-root .delivery-banner {
      background: ${C.green}; color: #fff; text-align: center; padding: 16px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px;
    }

    /* Fondateur */
    .ab-root .fondateur {
      position: relative; overflow: hidden; isolation: isolate;
      background: linear-gradient(135deg, ${C.navyDeep}, ${C.navy});
      color: #fff; padding: 96px 24px; text-align: center;
    }
    .ab-root .fondateur .orb-3 { width: 480px; height: 480px; background: ${C.orange}; top: -180px; left: -150px; opacity: 0.22; border-radius: 50%; filter: blur(80px); position: absolute; z-index: 0; }
    .ab-root .fondateur .orb-4 { width: 420px; height: 420px; background: ${C.navyMid}; bottom: -180px; right: -150px; opacity: 0.4; border-radius: 50%; filter: blur(80px); position: absolute; z-index: 0; }
    .ab-root .fondateur .inner { position: relative; z-index: 2; max-width: 880px; margin: 0 auto; }
    .ab-root .fondateur .badge-line {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 16px; background: rgba(249,115,22,0.18); color: ${C.orange};
      border: 1px solid rgba(249,115,22,0.3); border-radius: 999px;
      font-size: 13px; font-weight: 700; letter-spacing: 0.04em;
    }
    .ab-root .fondateur .badge-line .dot { width: 6px; height: 6px; border-radius: 50%; background: ${C.orange}; box-shadow: 0 0 8px ${C.orange}; }
    .ab-root .fondateur .display { margin-top: 26px; }
    .ab-root .fondateur .pills { margin-top: 36px; }
    .ab-root .fondateur .desc { margin-top: 28px; font-size: 17px; color: rgba(255,255,255,0.82); line-height: 1.6; }
    .ab-root .fondateur .cta-line { margin-top: 36px; display: flex; justify-content: center; }
    .ab-root .fondateur .note { margin-top: 18px; font-size: 13px; color: rgba(255,255,255,0.6); }
  `}</style>
);

// ══════════════════════════════════════════════════════════════════════════════
// HERO PRINCIPAL — PAGE MÈRE ESPACE PRO
// Centre Commercial Virtuel AbracadaWorld + Cards Flottantes
// ══════════════════════════════════════════════════════════════════════════════
const Hero = () => {
  // Image SVG du centre commercial
  const BUILDING_IMAGE = "https://customer-assets.emergentagent.com/job_organize-smart/artifacts/swq35zha_abracadaworld-01-test-world.svg";
  
  return (
    <section id="hero" className="hero-main">
      {/* Glow orange subtil */}
      <div className="orb-glow"></div>
      {/* Texture légère */}
      <div className="grid-texture"></div>
      
      {/* Main Grid - Left Text / Right Visual */}
      <div className="inner">
        {/* ═══════════════════════════════════════════════════════════════════
            ZONE GAUCHE — MESSAGE MARKETING
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="left">
          {/* Badge */}
          <div className="badge-main">
            Le 1er centre commercial virtuel du bâtiment
          </div>
          
          {/* Titre principal */}
          <h1 className="title-main">
            <span className="line-big">Loue ta boutique dans</span>
            <span className="line-medium">le centre commercial virtuel.</span>
          </h1>
          
          {/* Phrase d'impact orange */}
          <span className="impact-orange">0% commission, aucun intermédiaire.</span>
          
          {/* Trait orange séparateur */}
          <div className="orange-separator"></div>
          
          {/* Sous-texte explicatif - plus visible */}
          <p className="subtext" style={{ 
            fontSize: 'clamp(17px, 1.8vw, 20px)', 
            color: 'rgba(255,255,255,0.95)',
            fontWeight: 500,
            marginBottom: '24px'
          }}>
            Vends tes produits et tes prestations de service via ta boutique ou ton site internet.
          </p>
          
          {/* Prix */}
          <div style={{ marginBottom: '24px' }}>
            <div className="price-line" style={{ 
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(18px, 2.5vw, 28px)',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              letterSpacing: '0.02em'
            }}>
              À partir de <span style={{ color: '#F97316', fontWeight: 700 }}>50€</span> /mois
            </div>
            <div className="hidden sm:block" style={{ 
              fontSize: '14px', 
              color: 'rgba(255,255,255,0.7)',
              marginTop: '6px'
            }}>
              Prix mensuel pour un abonnement annuel
            </div>
          </div>
          
          {/* CTAs */}
          <div className="ctas-main" style={{ justifyContent: 'center', display: 'flex', width: '100%' }}>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="cta-green" style={{ width: 'auto' }} data-testid="hero-cta-fondateur">
              ✨ Voir les offres Fondateur
            </a>
          </div>
          
          {/* Mini badges de réassurance - 3 badges sur une ligne */}
          <div className="mini-badges" style={{ justifyContent: 'center', flexWrap: 'nowrap', gap: '8px' }}>
            <div className="mini-badge green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>100% chez toi</span>
            </div>
            <div className="mini-badge blue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
              <span>Cockpit inclus</span>
            </div>
            <div className="mini-badge orange">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              <span>IA intégrée</span>
            </div>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════
            ZONE DROITE — CENTRE COMMERCIAL + 3 CARDS EN LIGNE DESSOUS
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="right">
          {/* Badge mobile - affiché uniquement sur mobile au-dessus de l'image */}
          <div className="mobile-badge" style={{ display: 'none' }}>
            <div className="badge-main">
              Le 1er centre commercial virtuel du bâtiment
            </div>
          </div>
          
          <div className="building-container">
            {/* Glow subtil derrière le bâtiment */}
            <div className="building-glow"></div>
            
            {/* Image du centre commercial détourée */}
            <img 
              src={BUILDING_IMAGE} 
              alt="Centre Commercial Virtuel AbracadaWorld" 
              className="building-image"
            />
            
            {/* 4 Cards en ligne sous le bâtiment */}
            <div className="cards-row">
              {/* Card Boutique */}
              <div className="float-card boutique">
                <div className="fc-icon orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div className="fc-title">Boutique</div>
                <div className="fc-desc">Produits + prestations<br/><span className="fc-highlight">Sans commission</span></div>
              </div>
              
              {/* Card Cockpit */}
              <div className="float-card cockpit">
                <div className="fc-icon blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M3 9h18M9 21V9"/>
                  </svg>
                </div>
                <div className="fc-title">Cockpit</div>
                <div className="fc-desc">Centre de pilotage<br/><span className="fc-highlight">Boosté par l'IA</span></div>
              </div>
              
              {/* Card Équipe IA */}
              <div className="float-card agents">
                <div className="fc-icon green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                </div>
                <div className="fc-title">Équipe IA</div>
                <div className="fc-desc">Super Assistant<br/><span className="fc-highlight">+ agents spécialisés</span></div>
              </div>
              
              {/* Card Marketing */}
              <div className="float-card marketing">
                <div className="fc-icon purple">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                </div>
                <div className="fc-title">Marketing</div>
                <div className="fc-desc">Fais revenir<br/>tes clients</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES
// ══════════════════════════════════════════════════════════════════════════════
const ECOSYSTEM = [
  { ico: '🏪', t: 'Boutique virtuelle', d: "Ta boutique dans le centre commercial AbracadaWorld : produits, prestations, demandes, avis, contenu et visibilité dans ton univers métier.", badge: 'gratuit-pro' },
  { ico: '🌐', t: 'Site internet dédié', d: "Ton propre site avec ton nom de domaine, ton identité, tes pages, tes produits, tes prestations et ta boutique connectée.", badge: 'business-premium' },
  { ico: '🤖', t: 'Vendeur IA dans ta boutique', d: "Formé à ton activité, à tes produits, à tes prestations, il accueille, renseigne, conseille et aide tes clients à acheter.", badge: 'pro-business-premium' },
  { ico: '🔒', t: 'Paiement sécurisé', d: "Dans le centre commercial AbracadaWorld, les paiements passent par Stripe. L'argent est sécurisé puis débloqué quand le produit est livré ou la prestation réalisée. Acheteur et vendeur sont protégés.", badge: 'toutes-formules' },
  { ico: '💳', t: 'Encaissement personnel', d: "En formule Premium, ton site dédié peut être connecté à ton propre système d'encaissement. Tes clients paient directement sur ton compte via ton site avec ton module, tes règles et tes conditions.", badge: 'premium' },
  { ico: '🚀', t: 'Cockpit', d: "Un centre de pilotage adapté à ton métier pour gérer tes clients, ton marketing, ton commercial, tes équipes, ton administratif et toute ton activité avec l'aide de l'IA.", badge: 'toutes-formules' },
  { ico: '⚡', t: 'Super Assistant IA', d: "Il t'aide à gérer ton quotidien : agenda, mails, rendez-vous, prospects, administratif, devis, comptes rendus, relances clients, etc.", badge: 'pro-business-premium' },
  { ico: '👥', t: 'Équipe de 4 agents IA', d: "Service client, commercial, administratif et community manager. Formés à ton activité, paramétrés pour toi, ils travaillent ensemble sous la direction du Super Assistant.", badge: 'business-premium' },
  { ico: '🌟', t: 'Dream Team d\'agents IA', d: "Agent dédié à ton métier (ex : gestionnaire de missions, responsable logistique e-commerce...), modérateur, graphiste, webmaster, conseil juridique, analyste financier, monteur vidéo, marketing. Tous interconnectés, ils travaillent main dans la main pour toi.", badge: 'premium' },
  { ico: '💬', t: 'Réseau social thématique', d: "Sur AbracadaWorld, chaque univers a son propre réseau social : bâtiment, automobile, beauté... Un réseau bienveillant où 1 personne = 1 compte = zéro faux compte. Ton site a aussi son réseau social, et quand tu postes, tout est synchronisé sur tous tes autres réseaux sociaux.", badge: 'toutes-formules' },
  { ico: '📣', t: 'Marketing digital centralisé', d: "Tous tes réseaux sociaux centralisés dans un seul endroit. Publie, planifie, réponds aux messages ou aux commentaires. Seul ou avec l'aide de tes Community Manager IA.", badge: 'pro-business-premium' },
  { ico: '🔎', t: 'Référencement optimisé', d: "Un agent IA SEO analyse ton activité, ta cible, ton site, les tendances et les bons mots-clés. Il guide ensuite l'agent IA rédacteur web pour créer des articles optimisés pour Google et les moteurs IA.", badge: 'premium' },
  { ico: '⭐', t: 'Modération & Réputation', d: "Un agent IA effectue une veille et surveille ce qui se dit sur ton entreprise, puis répond à tous tes avis Google, Trustpilot et autres commentaires clients. Chaque avis est traité rapidement pour protéger ton image professionnelle, car on ne joue pas avec la réputation d'une entreprise.", badge: 'premium' },
  { ico: '💼', t: 'Emploi thématique', d: "Chaque univers AbracadaWorld possède son espace emploi dédié. Depuis ton cockpit, tu publies des offres de stage, CDI, CDD, mission ou autre à des candidats certifiés de ton secteur qui peuvent répondre directement.", badge: 'toutes-formules' },
  { ico: '🤝', t: 'Réseau Pro', d: "Chaque univers AbracadaWorld possède son propre réseau professionnel : Bâtiment, Beauté, Voyage, Immobilier, etc. Tu échanges avec des pros de ton métier qui parlent le même langage que toi.", badge: 'toutes-formules' },
  { ico: '🛒', t: "Centrale d'achat", d: "Les pros d'un même univers se regroupent pour négocier de meilleurs tarifs auprès des fournisseurs. Plus vous êtes nombreux, plus la centrale d'achat devient puissante et plus vos conditions d'achat s'améliorent.", badge: 'toutes-formules' },
];

// Agents Business (inclus dans abonnement Business)
// Agent Pro (inclus dans formule Pro)
const AGENT_PRO = [
  { e: '⚡', t: 'Super Assistant', r: "Chef d'orchestre — Il supervise toute ton équipe IA", d: "Il dirige et coordonne tous tes agents IA. Tu peux le consulter pour tout, à n'importe quel moment. 7j/7, 24h/24." },
];

const AGENTS_BUSINESS = [
  { e: '🎧', t: 'Service Client', r: 'Tes clients toujours satisfaits', d: "Répond aux questions, gère les réclamations, suit les demandes en cours. Disponible 24h/24." },
  { e: '💼', t: 'Commercial', r: 'Tes prospects et devis gérés', d: "Qualifie tes leads, rédige tes devis, programme les relances, tient ton pipeline à jour." },
  { e: '📋', t: 'Administratif', r: 'Zéro paperasse pour toi', d: "Devis signé → facture générée. Paiement dû → relance envoyée. Il classe et archive tout." },
  { e: '📣', t: 'Community Manager', r: 'Ta présence en ligne pilotée', d: "Répond à tes commentaires et DMs, programme tes publications sur tous tes réseaux." },
];

// Agents Premium (inclus dans formule Premium)
const AGENTS_PREMIUM = [
  { e: '📋', t: 'Gestionnaire de Missions', r: 'Tes prestations sous contrôle', d: "Ouvre la mission dès le devis signé, suit l'avancement en temps réel, gère le planning et les équipes." },
  { e: '🛡️', t: 'Modérateur', r: 'Ta réputation protégée 24h/24', d: "Surveille ce qui se dit sur toi, répond aux avis, détecte les bad buzz avant qu'ils explosent." },
  { e: '🎨', t: 'Graphiste', r: 'Tes visuels au niveau pro', d: "Crée tes visuels réseaux, tes flyers, tes supports clients. Respect de ta charte. Prêt à l'emploi." },
  { e: '💻', t: 'Webmaster', r: 'Ton site qui évolue tout seul', d: "Met à jour tes pages, crée tes landing pages, optimise ton SEO en continu." },
  { e: '⚖️', t: 'Conseil Juridique', r: 'Ton expert légal personnel', d: "Vérifie tes contrats, rédige tes CGV, surveille ta conformité RGPD." },
  { e: '📊', t: 'Analyste Financier', r: 'Ta santé financière en clair', d: "Analyse tes bilans, calcule tes marges par mission, anticipe ta trésorerie, reporting mensuel automatique." },
  { e: '🎬', t: 'Monteur Vidéo', r: 'Tes réalisations en vidéos virales', d: "Tu filmes avec ton téléphone. 30 secondes plus tard, clips courts prêts sur TikTok, Reels et YouTube Shorts." },
  { e: '📈', t: 'Marketing', r: 'Ta croissance pilotée', d: "Stratégie d'acquisition, campagnes, mesure ROI, ajustement en continu." },
];

const AGENTS = [...AGENTS_BUSINESS, ...AGENTS_PREMIUM];

const BOUTIQUE_HERO_FEATURES = [
  { icon: '🏪', title: 'Ta boutique', desc: 'Vitrine, produits, prestations, paiement en ligne.' },
  { icon: '🌐', title: 'Ton site internet', desc: 'Ton domaine, ton design, ton identité.' },
  { icon: '📱', title: 'Ton réseau social', desc: 'Feed, vidéos, salons, communauté engagée.' },
  { icon: '🤖', title: 'Ton équipe IA', desc: 'Assistant + jusqu\'à 12 agents qui travaillent pour toi.' },
  { icon: '💳', title: 'Tes paiements', desc: 'Stripe ou ton module propre. 0% de commission.' },
  { icon: '📝', title: 'Ton blog', desc: 'Articles, actualités, conseils pour tes clients.' },
];

const BOUTIQUE_HERO_STATS = [
  { value: '2', label: 'Portes d\'entrée' },
  { value: '0%', label: 'Commission' },
  { value: '24/24', label: 'Assistant IA' },
  { value: '7', label: 'Jours d\'installation' },
];

const COCKPIT_HERO_FEATURES = [
  { icon: '🎙️', title: 'Commande vocale', desc: 'Devis, tâches, comptes rendus... à la voix' },
  { icon: '📍', title: 'Suivi missions GPS', desc: 'Temps réel, rentabilité, pointage équipe' },
  { icon: '🤖', title: 'Équipe IA intégrée', desc: 'Assistant, commercial, gestionnaire...' },
  { icon: '📊', title: 'Pilotage 360°', desc: 'Activité, finance, équipe, marketing, achats...' },
  { icon: '🛡️', title: 'Zéro perte d\'info', desc: 'Tout est enregistré, classé, retrouvé' },
];

const COCKPIT_HERO_STATS = [
  { value: '1', label: 'Cockpit unique' },
  { value: '360°', label: 'Vision complète' },
  { value: '24/24', label: 'IA disponible' },
];

const AGENTS_HERO_FEATURES = [
  { icon: '⚡', title: 'Super Assistant', desc: 'Chef d\'orchestre — Il supervise toute ton équipe IA' },
  { icon: '💼', title: 'Commercial', desc: 'Qualifie tes leads, rédige tes devis, programme les relances' },
  { icon: '📋', title: 'Administratif', desc: 'Devis signé → facture générée. Paiement dû → relance envoyée' },
  { icon: '📋', title: 'Gestionnaire de Missions', desc: 'Suit l\'avancement en temps réel, gère le planning' },
  { icon: '📣', title: 'Community Manager', desc: 'Répond aux commentaires, programme tes publications' },
];

const AGENTS_HERO_STATS = [
  { value: '12', label: 'Agents IA' },
  { value: '7j/7', label: 'Disponibles' },
  { value: '24/24', label: 'Non-stop' },
  { value: '0€', label: 'Charges sociales' },
];

// ══════════════════════════════════════════════════════════════════════════════
// SECTIONS
// ══════════════════════════════════════════════════════════════════════════════

// Hero Boutique — Copie exacte du Hero de BoutiquePage.jsx
const BoutiqueHeroSection = () => (
  <section style={{ 
    background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative', 
    overflow: 'hidden',
    padding: 'clamp(80px, 8vw, 100px) clamp(20px, 5vw, 40px) clamp(40px, 5vw, 70px)',
  }}>
    {/* Glow orange subtil */}
    <div style={{ 
      position: 'absolute', 
      right: '5%', 
      top: '55%', 
      transform: 'translateY(-50%)',
      width: '700px',
      height: '700px',
      background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, rgba(249, 115, 22, 0.03) 35%, transparent 55%)',
      filter: 'blur(100px)',
      zIndex: 0,
      pointerEvents: 'none'
    }} />
    
    {/* Texture légère */}
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E\")",
      opacity: 0.03,
      zIndex: 0,
      pointerEvents: 'none'
    }} />
    
    <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
      <style>{`
        .boutique-hero-grid { display: grid; grid-template-columns: 1.5fr 0.55fr 0.75fr; gap: clamp(20px, 2.5vw, 40px); align-items: start; }
        .boutique-hero-stats { display: flex; gap: 0; }
        @media (max-width: 1100px) {
          .boutique-hero-grid { grid-template-columns: 1fr 1fr; }
          .boutique-hero-phone { display: none !important; }
        }
        @media (max-width: 768px) {
          .boutique-hero-grid { grid-template-columns: 1fr; text-align: center; }
          .boutique-hero-features { display: none !important; }
          .boutique-hero-stats { display: none !important; }
          .boutique-hero-phone-mobile { display: flex !important; }
          .hero-badge-desktop { display: none !important; }
          .hero-badge-mobile { display: inline-flex !important; margin: 0 auto 28px auto; }
          .hero-title-desktop { display: none !important; }
          .hero-title-mobile { display: block !important; }
          .hero-subtitle-desktop { display: none !important; }
          .hero-mobile-intro { display: flex !important; }
          .hero-bienvenue-mobile { display: block !important; }
          .hero-features-mobile { display: grid !important; }
          .hero-stats-mobile { display: flex !important; }
          .hero-sous-titre-desktop { display: none !important; }
          .hero-prix-desktop { display: none !important; }
          .hero-cta-desktop { display: none !important; }
        }
      `}</style>
      <div className="boutique-hero-grid">
        
        {/* ═══ COLONNE GAUCHE — Texte principal ═══ */}
        <div>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hero-badge-desktop"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 999,
              background: 'rgba(249, 115, 22, 0.15)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              color: C.orange,
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 28,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />
            Zéro commission, zéro intermédiaire.
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hero-badge-mobile"
            style={{
              display: 'none',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 999,
              background: 'rgba(249, 115, 22, 0.15)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              color: C.orange,
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 28,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />
            Zéro commission, zéro intermédiaire.
          </motion.div>

          {/* Titre */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="hero-title-desktop"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(36px, 4.5vw, 64px)',
              lineHeight: 1.1,
              color: C.navy,
              marginBottom: 12,
            }}
          >
            Ta boutique virtuelle<br />à ton nom.
          </motion.h2>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="hero-title-mobile"
            style={{
              display: 'none',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: 28,
              lineHeight: 1.15,
              color: C.navy,
              marginBottom: 12,
            }}
          >
            Ta boutique virtuelle<br />à ton nom.
          </motion.h2>
          
          {/* Sous-titre accrocheur - Desktop only */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hero-subtitle-desktop"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(20px, 2.4vw, 32px)',
              color: C.orange,
              marginBottom: 24,
            }}
          >
            Tu vends, tu encaisses, tu gardes 100%.
          </motion.p>
          
          {/* Mobile: Trait orange + texte */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hero-mobile-intro"
            style={{
              display: 'none',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: 24,
            }}
          >
            <div style={{
              width: 60,
              height: 4,
              background: C.orange,
              borderRadius: 2,
              marginBottom: 16,
            }} />
            <p style={{
              fontSize: 15,
              lineHeight: 1.5,
              color: C.gray600,
              textAlign: 'center',
              margin: 0,
            }}>
              Ta boutique virtuelle aménagée en 7 jours dans le 1<sup>er</sup> centre commercial virtuel.
            </p>
          </motion.div>
          
          {/* Image téléphone mobile uniquement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="boutique-hero-phone-mobile"
            style={{ 
              display: 'none', 
              justifyContent: 'center', 
              marginBottom: 24,
            }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/d8o1jy5w_15643.png"
              alt="Aperçu boutique mobile"
              style={{
                maxWidth: 280,
                width: '100%',
                height: 'auto',
                filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.2))',
              }}
            />
          </motion.div>
          
          {/* Bienvenue chez toi - Mobile only */}
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="hero-bienvenue-mobile"
            style={{
              display: 'none',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 20,
              color: C.orange,
              marginBottom: 20,
            }}
          >
            Bienvenue chez toi.
          </motion.h3>
          
          {/* Features grid - Mobile only */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="hero-features-mobile"
            style={{
              display: 'none',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 12,
              marginBottom: 24,
              width: '100%',
            }}
          >
            {BOUTIQUE_HERO_FEATURES.map((f, i) => (
              <div key={i} style={{
                background: '#fff',
                border: i === 1 ? `2px solid ${C.orange}` : `1px solid ${C.gray200}`,
                borderRadius: 12,
                padding: 14,
                textAlign: 'center',
                position: 'relative',
              }}>
                {i === 1 && (
                  <span style={{
                    position: 'absolute',
                    top: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                    color: '#fff',
                    fontSize: 7,
                    fontWeight: 700,
                    padding: '3px 6px',
                    borderRadius: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                  }}>Formule Premium</span>
                )}
                <div style={{ fontSize: 24, marginBottom: 8 }}>{f.icon}</div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: 13, 
                  color: C.navy,
                  marginBottom: 4,
                }}>
                  {f.title}
                </div>
                <div style={{ 
                  fontSize: 11, 
                  color: C.gray500,
                  lineHeight: 1.4,
                }}>
                  {f.desc}
                </div>
              </div>
            ))}
          </motion.div>
          
          {/* Trait orange décoratif */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="hero-trait-orange"
            style={{
              width: 60,
              height: 4,
              background: C.orange,
              borderRadius: 2,
              marginBottom: 24,
            }}
          />
          
          {/* Stats Mobile - After features grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="hero-stats-mobile"
            style={{
              display: 'none',
              flexWrap: 'nowrap',
              justifyContent: 'center',
              gap: 0,
            }}
          >
            {[
              { value: '0%', label: 'Commission' },
              { value: 'Zéro', label: 'intermédiaire' },
              { value: '24/24', label: 'Assistant IA' },
            ].map((stat, i) => (
              <div 
                key={i} 
                style={{ 
                  textAlign: 'center',
                  padding: '0 14px',
                  borderLeft: i > 0 ? `1px solid ${C.gray200}` : 'none',
                }}
              >
                <div style={{ 
                  fontFamily: "'Plus Jakarta Sans', sans-serif", 
                  fontWeight: 800, 
                  fontSize: 20, 
                  color: C.orange 
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 10, color: C.gray500, marginTop: 2 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>

          {/* Sous-titre - Desktop only */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="hero-sous-titre-desktop"
            style={{
              fontSize: 'clamp(15px, 1.8vw, 18px)',
              lineHeight: 1.6,
              color: C.gray600,
              marginBottom: 24,
              maxWidth: 520,
            }}
          >
            <p style={{ marginBottom: 8, color: C.gray600 }}>
              Ta boutique virtuelle aménagée en 7 jours dans le 1<sup>er</sup> centre commercial du bâtiment.
            </p>
            <p style={{ margin: 0, color: C.gray600 }}>
              2 portes d'entrée, 1 seul endroit : <span style={{ color: C.orange, fontWeight: 600 }}>chez toi.</span>
            </p>
          </motion.div>

          {/* Prix - Desktop only */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="hero-prix-desktop"
            style={{ marginBottom: 8 }}
          >
            <span style={{ color: C.gray600, fontSize: 16 }}>À partir de </span>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 42, color: C.navy }}>50€</span>
            <span style={{ color: C.orange, fontSize: 16, fontWeight: 600 }}>*</span>
            <span style={{ color: C.gray500, fontSize: 16 }}> / Mois</span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="hero-prix-desktop"
            style={{ 
              fontSize: 12, 
              color: C.gray500, 
              marginBottom: 28,
              fontStyle: 'italic',
            }}
          >
            *Prix mensuel pour un Abonnement Annuel
          </motion.p>

          {/* CTAs - Desktop only */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="hero-cta-desktop"
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 40 }}
          >
            <a
              href="#boutique"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '16px 28px',
                borderRadius: 12,
                background: C.orange,
                color: C.white,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
                transition: 'transform 0.2s',
              }}
            >
              En savoir plus →
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="boutique-hero-stats"
          >
            {BOUTIQUE_HERO_STATS.map((stat, i) => (
              <div 
                key={i} 
                style={{ 
                  textAlign: 'center',
                  padding: '0 20px',
                  borderLeft: i > 0 ? `1px solid ${C.gray200}` : 'none',
                }}
              >
                <div style={{ 
                  fontFamily: "'Plus Jakarta Sans', sans-serif", 
                  fontWeight: 800, 
                  fontSize: 28, 
                  color: C.orange 
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: C.gray500, marginTop: 4 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ═══ COLONNE CENTRALE — Liste de features ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="boutique-hero-features"
          style={{ paddingTop: 60 }}
        >
          <h3 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: C.orange,
            marginBottom: 28,
          }}>
            Bienvenue chez toi.
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {BOUTIQUE_HERO_FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(249, 115, 22, 0.1)',
                  border: '1px solid rgba(249, 115, 22, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ 
                    fontWeight: 700, 
                    fontSize: 15, 
                    color: C.navy,
                    marginBottom: 3,
                  }}>
                    {f.title}
                  </div>
                  <div style={{ 
                    fontSize: 13, 
                    color: C.gray500,
                    lineHeight: 1.5,
                  }}>
                    {f.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ═══ COLONNE DROITE — Phone Mockup Image ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="boutique-hero-phone"
          style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            alignSelf: 'center',
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/d8o1jy5w_15643.png"
            alt="Aperçu boutique mobile"
            style={{
              maxWidth: 480,
              width: '100%',
              height: 'auto',
              filter: 'drop-shadow(0 40px 80px rgba(0,0,0,0.25))',
            }}
          />
        </motion.div>
      </div>
    </div>
  </section>
);

// Hero Cockpit Bâtiment — Version pour la page de présentation
const CockpitHeroSection = () => (
  <section style={{ 
    background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative', 
    overflow: 'hidden',
    padding: 'clamp(80px, 8vw, 100px) clamp(20px, 5vw, 40px) clamp(40px, 5vw, 70px)',
  }}>
    {/* Glow orange subtil */}
    <div style={{ 
      position: 'absolute', 
      right: '5%', 
      top: '55%', 
      transform: 'translateY(-50%)',
      width: '700px',
      height: '700px',
      background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, rgba(249, 115, 22, 0.03) 35%, transparent 55%)',
      filter: 'blur(100px)',
      zIndex: 0,
      pointerEvents: 'none'
    }} />
    
    <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
      <style>{`
        .cockpit-hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(40px, 5vw, 80px); align-items: center; }
        .cockpit-hero-stats { display: flex; gap: 0; justify-content: center; }
        @media (max-width: 900px) {
          .cockpit-hero-grid { grid-template-columns: 1fr; }
          .cockpit-hero-image { display: none !important; }
          .cockpit-hero-stats { flex-wrap: nowrap; justify-content: center; }
          .cockpit-hero-grid > div:first-child { text-align: center; }
        }
      `}</style>
      <div className="cockpit-hero-grid">
        
        {/* ═══ COLONNE GAUCHE — Texte principal ═══ */}
        <div>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 999,
              background: 'rgba(249, 115, 22, 0.15)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              color: C.orange,
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 28,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />
            Centre de pilotage · IA intégrée
          </motion.div>

          {/* Titre */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(36px, 4.5vw, 64px)',
              lineHeight: 1.1,
              color: C.navy,
              marginBottom: 12,
              textAlign: 'center',
            }}
          >
            Ta boutique<br />virtuelle.
          </motion.h2>
          
          {/* Sous-titre accrocheur */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(18px, 2.8vw, 36px)',
              color: C.orange,
              marginBottom: 24,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            Pilotée depuis un seul cockpit.
          </motion.p>
          
          {/* Trait orange décoratif */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            style={{
              width: 60,
              height: 4,
              background: C.orange,
              borderRadius: 2,
              marginBottom: 24,
              margin: '0 auto 24px auto',
            }}
          />

          {/* Sous-titre */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            style={{
              fontSize: 'clamp(12px, 1.8vw, 18px)',
              lineHeight: 1.6,
              color: C.gray600,
              marginBottom: 24,
              maxWidth: 520,
              textAlign: 'center',
            }}
          >
            <p style={{ marginBottom: 8, color: C.gray600, whiteSpace: 'nowrap' }}>
              Missions, clients, équipes, marketing, ventes...
            </p>
            <p style={{ margin: 0, color: C.navy, fontWeight: 600, whiteSpace: 'nowrap', fontSize: 'clamp(11px, 1.6vw, 16px)' }}>
              Tout est centralisé, automatisé et piloté par l'IA.
            </p>
          </motion.div>

          {/* Points clés */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}
          >
            {[
              'Devis en 1 minute à la voix',
              'Suivi missions en temps réel',
              'IA + équipe = Zéro perte de temps',
              'Agenda — Bloque un RDV en 10 sec',
              'Tâches — Ne rate plus jamais rien'
            ].map((point, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, fontWeight: 600, color: C.navy }}>
                <span style={{ color: C.orange, fontSize: 18 }}>✓</span>
                {point}
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 40 }}
          >
            <a
              href="#crm"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '16px 28px',
                borderRadius: 12,
                background: C.orange,
                color: C.white,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
                transition: 'transform 0.2s',
              }}
            >
              En savoir plus →
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="cockpit-hero-stats"
          >
            {COCKPIT_HERO_STATS.map((stat, i) => (
              <div 
                key={i} 
                style={{ 
                  textAlign: 'center',
                  padding: '0 20px',
                  borderLeft: i > 0 ? `1px solid ${C.gray200}` : 'none',
                }}
              >
                <div style={{ 
                  fontFamily: "'Plus Jakarta Sans', sans-serif", 
                  fontWeight: 800, 
                  fontSize: 28, 
                  color: C.orange 
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: C.gray500, marginTop: 4 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ═══ COLONNE DROITE — Features ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="cockpit-hero-image"
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {COCKPIT_HERO_FEATURES.map((f, i) => (
            <div key={i} style={{ 
              display: 'flex', 
              gap: 16, 
              alignItems: 'flex-start',
              background: C.white,
              borderRadius: 16,
              padding: '20px 24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: `1px solid ${C.gray100}`,
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid rgba(249, 115, 22, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
              }}>
                {f.icon}
              </div>
              <div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: 16, 
                  color: C.navy,
                  marginBottom: 4,
                }}>
                  {f.title}
                </div>
                <div style={{ 
                  fontSize: 14, 
                  color: C.gray500,
                  lineHeight: 1.5,
                }}>
                  {f.desc}
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

// Hero Équipe Agents IA — Version pour la page de présentation
const AgentsHeroSection = () => (
  <section style={{ 
    background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative', 
    overflow: 'hidden',
    padding: 'clamp(80px, 8vw, 100px) clamp(20px, 5vw, 40px) clamp(40px, 5vw, 70px)',
  }}>
    {/* Glow orange subtil */}
    <div style={{ 
      position: 'absolute', 
      right: '5%', 
      top: '55%', 
      transform: 'translateY(-50%)',
      width: '700px',
      height: '700px',
      background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, rgba(249, 115, 22, 0.03) 35%, transparent 55%)',
      filter: 'blur(100px)',
      zIndex: 0,
      pointerEvents: 'none'
    }} />
    
    <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
      <style>{`
        .agents-hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(40px, 5vw, 80px); align-items: center; }
        .agents-hero-stats { display: flex; gap: 0; }
        @media (max-width: 900px) {
          .agents-hero-grid { grid-template-columns: 1fr; }
          .agents-hero-features { display: none !important; }
          .agents-hero-stats { flex-wrap: wrap; justify-content: center; }
          .agents-hero-grid > div:first-child { text-align: center; }
        }
      `}</style>
      <div className="agents-hero-grid">
        
        {/* ═══ COLONNE GAUCHE — Texte principal ═══ */}
        <div>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 999,
              background: 'rgba(249, 115, 22, 0.15)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              color: C.orange,
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 28,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />
            Équipe Agents IA
          </motion.div>

          {/* Titre */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(28px, 3.5vw, 52px)',
              lineHeight: 1.15,
              color: C.navy,
              marginBottom: 12,
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Un Super Assistant +</span><br />
            <span style={{ whiteSpace: 'nowrap' }}>une équipe d'Agents IA</span>
          </motion.h2>
          
          {/* Sous-titre accrocheur */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(20px, 2.4vw, 32px)',
              color: C.orange,
              marginBottom: 24,
            }}
          >
            Qui travaillent 7j/7, 24h/24
          </motion.p>
          
          {/* Trait orange décoratif */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            style={{
              width: 60,
              height: 4,
              background: C.orange,
              borderRadius: 2,
              marginBottom: 24,
              margin: '0 auto 24px auto',
            }}
          />

          {/* Sous-titre */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            style={{
              fontSize: 'clamp(15px, 1.8vw, 18px)',
              lineHeight: 1.6,
              color: C.gray600,
              marginBottom: 32,
              maxWidth: 520,
              margin: '0 auto 32px auto',
            }}
          >
            <p style={{ margin: 0, color: C.gray600 }}>
              Tu peux démarrer avec ton Super Assistant, puis activer une <span style={{ color: C.orange, fontWeight: 600 }}>équipe d'agents IA</span> spécialisés qui collaborent entre eux dans ton Cockpit.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 40 }}
          >
            <a
              href="#assistant"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '16px 28px',
                borderRadius: 12,
                background: C.orange,
                color: C.white,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
                transition: 'transform 0.2s',
              }}
            >
              En savoir plus →
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="agents-hero-stats"
          >
            {AGENTS_HERO_STATS.map((stat, i) => (
              <div 
                key={i} 
                style={{ 
                  textAlign: 'center',
                  padding: '0 20px',
                  borderLeft: i > 0 ? `1px solid ${C.gray200}` : 'none',
                }}
              >
                <div style={{ 
                  fontFamily: "'Plus Jakarta Sans', sans-serif", 
                  fontWeight: 800, 
                  fontSize: 28, 
                  color: C.orange 
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: C.gray500, marginTop: 4 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ═══ COLONNE DROITE — Features ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="agents-hero-features"
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {AGENTS_HERO_FEATURES.map((f, i) => (
            <div key={i} style={{ 
              display: 'flex', 
              gap: 16, 
              alignItems: 'flex-start',
              background: C.white,
              borderRadius: 16,
              padding: '20px 24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: `1px solid ${C.gray100}`,
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid rgba(249, 115, 22, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
              }}>
                {f.icon}
              </div>
              <div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: 16, 
                  color: C.navy,
                  marginBottom: 4,
                }}>
                  {f.title}
                </div>
                <div style={{ 
                  fontSize: 14, 
                  color: C.gray500,
                  lineHeight: 1.5,
                }}>
                  {f.desc}
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

const EcosystemeSection = () => (
  <section id="ecosysteme" className="section section-cream">
    <div className="container-narrow">
      <div className="section-head">
        <span className="eyebrow eyebrow-navy">L'écosystème complet</span>
        <h2 className="t-h2">Tout ce dont tu as besoin,<br /><span className="accent">enfin réuni.</span></h2>
        <p className="t-lead"><strong>AbracadaWorld</strong>, c'est bien plus qu'un site internet ou une application supplémentaire. C'est une <strong>boutique en ligne</strong> + un <strong>site internet</strong> et toutes les applications réunies en une seule, boostées par l'intelligence artificielle.</p>
      </div>
      <div className="eco-grid">
        {/* Séparateur "+" en overlay pour desktop */}
        <div className="eco-separator-desktop" style={{
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(232,101,10,0.35)',
            color: '#fff',
            fontSize: '24px',
            fontWeight: 700
          }}>+</div>
        </div>
        {ECOSYSTEM.map((it, i) => {
          // Afficher le séparateur mobile après Boutique en ligne (index 0)
          const showMobileSeparator = i === 1;
          return (
            <>
              {showMobileSeparator && (
                <div key={`sep-${i}`} className="eco-separator-mobile" style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 0'
                }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(232,101,10,0.35)',
                    color: '#fff',
                    fontSize: '24px',
                    fontWeight: 700
                  }}>+</div>
                </div>
              )}
              <div key={i} className={`eco-card${[0, 1, 5].includes(i) ? ' highlight-mobile' : ''}`} style={{ position: 'relative' }}>
              {it.badge === 'gratuit-pro' && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                  color: '#fff',
                  fontSize: '8px',
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 6px rgba(232,101,10,0.3)'
                }}>Formule Gratuite & Pro</span>
              )}
              {it.badge === 'business-premium' && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                  color: '#fff',
                  fontSize: '8px',
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 6px rgba(232,101,10,0.3)'
                }}>Formule Business & Premium</span>
              )}
              {it.badge === 'premium' && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                  color: '#fff',
                  fontSize: '8px',
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 6px rgba(232,101,10,0.3)'
                }}>Formule Premium</span>
              )}
              {it.badge === 'pro-business-premium' && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                  color: '#fff',
                  fontSize: '8px',
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 6px rgba(232,101,10,0.3)'
                }}>Formule Pro, Business & Premium</span>
              )}
              {it.badge === 'toutes-formules' && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
                  color: '#fff',
                  fontSize: '8px',
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 6px rgba(232,101,10,0.3)'
                }}>Toutes les formules</span>
              )}
              <div className="eco-icon">{it.ico}</div>
              <div className="eco-title">{it.t}</div>
              <div className="eco-desc">{it.d}</div>
              {it.badge === 'business-premium' && it.t === 'Site internet dédié' && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '12px',
                  background: 'rgba(232,101,10,0.08)',
                  border: '1px solid rgba(232,101,10,0.25)',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#E8650A'
                }}>
                  <span>🎁</span>
                  <span>Nom de domaine offert</span>
                </div>
              )}
            </div>
            </>
          );
        })}
      </div>
      <div className="world-banner">
        <img src="/abracadaworld-globe.svg" alt="AbracadaWorld" style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
        <div className="text">
          <div className="t"><span className="hide-mobile">AbracadaWorld — </span>20 univers thématiques</div>
          <div className="d">
            <strong>AbracadaWorld</strong> est le premier centre commercial virtuel.<br />
            <strong>Univers Bâtiment</strong> déjà ouvert.<br />
            Bientôt : Univers Maison, Voyage, Rencontre, Beauté, Mode, etc.<br />
            <strong>Un seul compte certifié te permet de naviguer partout.</strong>
          </div>
        </div>
        <button className="btn" style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.orangeDeep})`, color: '#fff', fontWeight: 700 }}>Découvrir AbracadaWorld →</button>
      </div>
    </div>
  </section>
);

const AssistantSection = () => (
  <section id="assistant" className="section section-cream">
    <div className="container">
      <div className="section-head" style={{ '--mb': '64px' }}>
        <span className="eyebrow eyebrow-orange"><span className="dot" style={{ background: C.orange }}></span>La star de la plateforme</span>
        <h2 className="t-h2">Ton Super Assistant IA<br /><span className="accent">travaille pendant que toi tu bosses.</span></h2>
        <p className="t-lead">Tu travailles sur le terrain. Lui il gère le reste. Devis, clients, réseaux sociaux, factures, site internet. Tout.</p>
      </div>
      <div className="assist-grid">
        <div className="assist-features">
          <div className="assist-feat">
            <div className="ico">📅</div>
            <div><div className="t">Assistant de direction</div><div className="d">Agenda, emails, tâches et priorités. Ton bras droit — 7j/7, 24h/24.</div></div>
          </div>
          <div className="assist-feat">
            <div className="ico">👥</div>
            <div><div className="t">Gestion de l'équipe IA</div><div className="d">Il supervise, coordonne et fait travailler tous tes agents IA.</div></div>
          </div>
          <div className="assist-feat">
            <div className="ico">📝</div>
            <div><div className="t">Compte-rendu Pro</div><div className="d">Mission, réunion, visite client — rédigé automatiquement.</div></div>
          </div>
          <div className="assist-feat orange">
            <div className="ico">🎤</div>
            <div><div className="t">Parle-lui directement</div><div className="d">Devis, compte-rendu, résumé de RDV — tu parles, il gère.</div></div>
          </div>
        </div>
        <div>
          <div className="phone-frame">
            <div className="chat">
              <div className="chat-hdr">
                <div className="chat-av">⚡</div>
                <div className="chat-hdr-text">
                  <div className="name">Super Assistant AbracadaWorld</div>
                  <div className="sub">Disponible 24h/24 · 7j/7</div>
                </div>
                <div className="chat-online"><div className="dot-green"></div>En ligne</div>
              </div>
              <div className="chat-body">
                <div className="msg user"><div className="av">R</div><div className="bubble user">Fais-moi un devis pour M. Dupont, rénovation salle de bain, environ 4 500€ HT</div></div>
                <div className="msg"><div className="av">🪄</div><div className="bubble bot">Devis créé ✓ Envoyé à M. Dupont par email et SMS. Je te notifie dès qu'il répond.</div></div>
                <div className="msg user"><div className="av">R</div><div className="bubble user">Publie une photo de ma dernière réalisation chez les Martin</div></div>
                <div className="msg"><div className="av">🪄</div><div className="bubble bot">Photo publiée sur AbracadaWorld, Facebook et Instagram avec légende rédigée ✓</div></div>
                <div className="msg user"><div className="av">R</div><div className="bubble user">Relance les factures impayées</div></div>
                <div className="msg"><div className="av">🪄</div><div className="bubble bot">3 relances envoyées : Martin 850€, Leroy 1 200€, Garcia 620€. Total : 2 670€ ✓</div></div>
              </div>
              <div className="chat-input">
                <button className="chat-btn">+</button>
                <div className="chat-input-field">Écris ton message…</div>
                <button className="chat-btn">🎤</button>
                <button className="chat-mic">➤</button>
              </div>
            </div>
          </div>
          <p className="phone-caption">Disponible depuis ton téléphone, où que tu sois</p>
        </div>
      </div>
    </div>
  </section>
);

const AgentsSection = () => (
  <section id="agents" className="section section-cream">
    <div className="container-narrow">
      <div className="section-head">
        <span className="eyebrow eyebrow-orange">Ton équipe complète</span>
        <h2 className="t-h2" style={{ whiteSpace: 'nowrap' }}>Ton équipe IA <span className="accent">complète.</span></h2>
        <p className="t-lead">13 agents spécialisés. 1 super assistant qui les coordonne. <strong>Ils travaillent ensemble pendant que toi tu bosses.</strong></p>
      </div>

      {/* Agent Pro - Super Assistant */}
      <div style={{ marginBottom: 56 }}>
        <div style={{
          textAlign: 'center',
          marginBottom: 16,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 16,
            padding: '16px 32px',
            background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
            border: `2px solid ${C.orange}`,
            borderRadius: 16,
            boxShadow: '0 4px 12px rgba(249,115,22,0.12)',
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${C.orange}, ${C.orangeDeep})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
            }}>
              ⚡
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: 18,
                color: C.orange,
              }}>
                Inclus dans la formule Pro
              </div>
              <div style={{ fontSize: 13, color: C.gray600, marginTop: 2 }}>
                Ton Super Assistant boosté à l'IA
              </div>
            </div>
          </div>
          {/* Flèche vers le bas */}
          <div style={{ marginTop: 12, color: C.orange, fontSize: 24 }}>▼</div>
        </div>
        <div className="agt-grid" style={{ maxWidth: 400, margin: '0 auto' }}>
          {AGENT_PRO.map((a, i) => (
            <div key={i} className="agt-card" style={{ border: `2px solid ${C.orange}` }}>
              <span className="agt-emoji">{a.e}</span>
              <div className="agt-title">{a.t}</div>
              <div className="agt-role">{a.r}</div>
              <div className="agt-desc">{a.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Agents Business */}
      <div style={{ marginBottom: 56 }}>
        <div style={{
          textAlign: 'center',
          marginBottom: 16,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 16,
            padding: '16px 32px',
            background: '#fff',
            border: `2px solid ${C.navy}`,
            borderRadius: 16,
            boxShadow: '0 4px 12px rgba(30,58,95,0.08)',
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: C.navy,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
            }}>
              💼
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: 18,
                color: C.navy,
              }}>
                Inclus dans la formule Business
              </div>
              <div style={{ fontSize: 13, color: C.gray500, marginTop: 2 }}>
                4 agents supplémentaires pour démarrer
              </div>
            </div>
          </div>
          {/* Flèche vers le bas */}
          <div style={{ marginTop: 12, color: C.navy, fontSize: 24 }}>▼</div>
        </div>
        <div className="agt-grid">
          {AGENTS_BUSINESS.map((a, i) => (
            <div key={i} className="agt-card" style={{ border: `2px solid ${C.navy}` }}>
              <span className="agt-emoji">{a.e}</span>
              <div className="agt-title">{a.t}</div>
              <div className="agt-role">{a.r}</div>
              <div className="agt-desc">{a.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Agents Premium */}
      <div style={{ marginBottom: 56 }}>
        <div style={{
          textAlign: 'center',
          marginBottom: 16,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 16,
            padding: '16px 32px',
            background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
            border: `2px solid ${C.orange}`,
            borderRadius: 16,
            boxShadow: '0 4px 12px rgba(249,115,22,0.12)',
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${C.orange}, ${C.orangeDeep})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
            }}>
              👑
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: 18,
                color: C.orange,
              }}>
                Inclus dans la formule Premium
              </div>
              <div style={{ fontSize: 13, color: C.gray500, marginTop: 2 }}>
                8 agents IA supplémentaires
              </div>
            </div>
          </div>
          {/* Flèche vers le bas */}
          <div style={{ marginTop: 12, color: C.orange, fontSize: 24 }}>▼</div>
        </div>
        <div className="agt-grid">
          {AGENTS_PREMIUM.map((a, i) => (
            <div key={i} className="agt-card" style={{ border: `2px solid ${C.orange}` }}>
              <span className="agt-emoji">{a.e}</span>
              <div className="agt-title">{a.t}</div>
              <div className="agt-role">{a.r}</div>
              <div className="agt-desc">{a.d}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="magic-banner">
        <div className="kicker">✨ La magie AbracadaWorld</div>
        <div className="head">Appelle-les comme tu veux.</div>
        <p className="body">Ton Commercial peut s'appeler <strong>Jacques</strong>. Ton Gestionnaire de Missions peut s'appeler <strong>Mohamed</strong>. C'est ta vraie équipe. Tes règles.</p>
      </div>
    </div>
  </section>
);

const CrmSection = () => (
  <section id="crm" className="section section-cream">
    <div className="container">
      <div className="section-head">
        <span className="eyebrow eyebrow-navy">Inclus dans ton abonnement</span>
        <h2 className="t-h2">Ton centre de pilotage<br /><span className="accent">100% paramétré pour toi.</span></h2>
        <p className="t-lead">Piloté par la voix, boosté par l'IA, gagne du temps, gagne de l'argent. Grâce à ton cockpit, tout devient plus simple, tu ne rates plus jamais rien.</p>
      </div>

      <div className="cockpit-shell">
        <aside className="cockpit-side">
          <div className="cockpit-side-head">
            <div className="cockpit-side-brand">
              <div className="cockpit-side-icon">🏗️</div>
              <div>
                <div className="cockpit-side-name">Cockpit Pro</div>
                <div className="cockpit-side-sub">Assistant IA intégré</div>
              </div>
            </div>
            <div className="cockpit-newchat">+ Nouvelle conversation</div>
          </div>
          <div className="cockpit-menu">
            <div className="cockpit-mi active"><div className="ico">👥</div><span className="lbl">Collaboration</span><span className="chev">∧</span></div>
            <div className="cockpit-submi">🏠 Tableau de bord</div>
            <div className="cockpit-submi">👤 Mon Équipe</div>
            <div className="cockpit-submi">✓ Tâches partagées</div>
            <div className="cockpit-submi">📅 Agenda partagé</div>
            <div className="cockpit-mi"><div className="ico">🎯</div><span className="lbl">Commercial</span><span className="chev">∨</span></div>
            <div className="cockpit-mi"><div className="ico">📦</div><span className="lbl">Catalogue / E-commerce</span><span className="chev">∧</span></div>
            <div className="cockpit-submi">🛍️ Mes produits</div>
            <div className="cockpit-submi">🔧 Mes prestations</div>
            <div className="cockpit-submi">📂 Catégories</div>
            <div className="cockpit-submi">📊 Stock et disponibilité</div>
            <div className="cockpit-submi">🏷️ Promotions</div>
            <div className="cockpit-submi">⚡ Ventes flash</div>
            <div className="cockpit-submi">📦 Packs</div>
            <div className="cockpit-submi">📥 Import CSV</div>
            <div className="cockpit-mi"><div className="ico">📋</div><span className="lbl">Missions</span><span className="chev">∨</span></div>
            <div className="cockpit-mi"><div className="ico">📣</div><span className="lbl">Marketing</span><span className="chev">∨</span></div>
            <div className="cockpit-mi"><div className="ico">🌐</div><span className="lbl">Réseau Social</span><span className="chev">∨</span></div>
            <div className="cockpit-mi muted"><div className="ico">🔗</div><span className="lbl">Réseau Pro</span><span className="badge-soon">À VENIR</span></div>
            <div className="cockpit-mi muted"><div className="ico">🛒</div><span className="lbl">Centrale d'achat</span><span className="badge-soon">À VENIR</span></div>
            <div className="cockpit-mi"><div className="ico">💼</div><span className="lbl">Emploi</span><span className="chev">∨</span></div>
            <div className="cockpit-mi"><div className="ico">🏪</div><span className="lbl">Ma Boutique</span><span className="chev">∨</span></div>
          </div>
        </aside>
        <div className="cockpit-main">
          {/* Desktop version */}
          <div className="hide-on-mobile">
            <div className="cockpit-greeting">✦ Salut</div>
            <div className="cockpit-hello-block">
              <div className="cockpit-mic">🎤</div>
              <div className="cockpit-hello-title">Bonjour toi !</div>
              <div className="cockpit-hello-sub">Appuie sur le micro pour parler, ou écris ton message</div>
            </div>
            <div className="cockpit-actions-lbl">Actions rapides</div>
            <div className="cockpit-actions">
              <div className="cockpit-action"><span className="ico">📋</span><div className="ttl">Compte rendu</div><div className="sub">Parle, je rédige</div></div>
              <div className="cockpit-action"><span className="ico">📄</span><div className="ttl">Créer un devis</div><div className="sub">Génère en questions</div></div>
              <div className="cockpit-action"><span className="ico">📅</span><div className="ttl">Mon agenda</div><div className="sub">Planning du jour</div></div>
              <div className="cockpit-action"><span className="ico">👥</span><div className="ttl">Prospects & clients</div><div className="sub">Suivi, relances</div></div>
              <div className="cockpit-action"><span className="ico">✉️</span><div className="ttl">Boîte mail</div><div className="sub">Tous tes mails</div></div>
              <div className="cockpit-action"><span className="ico">💬</span><div className="ttl">Messages</div><div className="sub">Commentaires</div></div>
            </div>
          </div>
          
          {/* Mobile version - Phone-like container with orange border */}
          <div className="show-on-mobile-only cockpit-mobile-phone-wrapper">
            <div className="cockpit-greeting">✦ Salut</div>
            <div className="cockpit-hello-block">
              <div className="cockpit-mic">🎤</div>
              <div className="cockpit-hello-title">Bonjour toi !</div>
              <div className="cockpit-hello-sub">Appuie sur le micro pour parler, ou écris ton message</div>
            </div>
            <div className="cockpit-actions-lbl">Actions rapides</div>
            <div className="cockpit-actions cockpit-actions-mobile">
              <div className="cockpit-action"><span className="ico">📋</span><div className="ttl">Compte rendu</div><div className="sub">Parle, je rédige</div></div>
              <div className="cockpit-action"><span className="ico">📄</span><div className="ttl">Créer un devis</div><div className="sub">Génère en questions</div></div>
              <div className="cockpit-action"><span className="ico">📅</span><div className="ttl">Mon agenda</div><div className="sub">Planning du jour</div></div>
              <div className="cockpit-action"><span className="ico">👥</span><div className="ttl">Clients</div><div className="sub">Suivi, relances</div></div>
              <div className="cockpit-action"><span className="ico">✉️</span><div className="ttl">Mes mails</div><div className="sub">Tous tes mails</div></div>
              <div className="cockpit-action"><span className="ico">✅</span><div className="ttl">Mes tâches</div><div className="sub">À faire aujourd'hui</div></div>
            </div>
            
            {/* Claude-style chat input */}
            <div className="cockpit-chat-claude">
              <div className="claude-input-wrapper">
                <input 
                  type="text" 
                  placeholder="Écris ton message..."
                  className="claude-input"
                  readOnly
                />
                <button className="claude-send-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="module-grid">
        {[
          { ic: '🏠', t: 'Mon espace', items: ['Mes messages', 'Mes mails', 'Mes contacts', 'Mon agenda', 'Mes tâches', 'Mes notes', 'Mes documents'] },
          { ic: '👥', t: 'Collaboration', items: ['Tableau de bord', 'Mon équipe', 'Chat équipe', 'Actualités équipes', 'Tâches partagées', 'Agenda partagé', 'Documents partagés'] },
          { ic: '🎯', t: 'Commercial', items: ['Pipeline commercial', 'Demandes de devis', 'Prospects', 'Clients', 'Devis', 'Factures'] },
          { ic: '📦', t: 'Catalogue / E-commerce', items: ['Mes produits', 'Mes prestations', 'Catégories', 'Stock et disponibilité', 'Promotions', 'Ventes flash', 'Packs', 'Import CSV'] },
          { ic: '📋', t: 'Missions', items: ['En cours', 'À venir & Terminées', 'Planning équipes', 'Pointage GPS'] },
          { ic: '📣', t: 'Marketing', items: ['Statistiques', 'Campagnes email', 'Publicités', 'Réseaux sociaux'] },
          { ic: '🌐', t: 'Réseau Social', items: ['Mon Feed', 'Mes Publications', 'Ma Galerie', 'Mes Abonnés', 'Messages'] },
        ].map((m, i) => (
          <div key={i} className="module">
            <div className="module-hdr"><div className="module-icon">{m.ic}</div><div className="module-title">{m.t}</div></div>
            <div className="module-list">
              {m.items.map((it, j) => <div key={j} className="module-item"><span className="arr">›</span>{it}</div>)}
            </div>
          </div>
        ))}
        <div className="module muted">
          <div className="module-hdr"><div className="module-icon">🔗</div><div className="module-title">Réseau Pro</div><span className="module-soon">À VENIR</span></div>
          <div className="module-hdr"><div className="module-icon">🛒</div><div className="module-title">Centrale d'achat</div><span className="module-soon">À VENIR</span></div>
        </div>
        <div className="module">
          <div className="module-hdr"><div className="module-icon">💼</div><div className="module-title">Emploi</div></div>
          <div className="module-list">
            <div className="module-item"><span className="arr">›</span>Rechercher</div>
            <div className="module-item"><span className="arr">›</span>Mes Offres</div>
            <div className="module-item"><span className="arr">›</span>Candidatures</div>
          </div>
        </div>
        <div className="module">
          <div className="module-hdr"><div className="module-icon">🏪</div><div className="module-title">Ma Boutique</div></div>
          <div className="module-list">
            <div className="module-item"><span className="arr">›</span>Informations & Logo</div>
            <div className="module-item"><span className="arr">›</span>Mentions Devis & CGV</div>
            <div className="module-item"><span className="arr">›</span>Horaires</div>
            <div className="module-item"><span className="arr">›</span>Blog & Articles</div>
          </div>
        </div>
      </div>

      <div className="cockpit-bottom-banner">
        <div>
          <div className="kicker">⚡ Assisté par l'IA à 100%</div>
          <div className="ttl">Tout est paramétré. Tout est prêt.</div>
          <div className="desc">Ton Super Assistant et tes agents IA pilotent chaque module. Tu travailles — eux gèrent.</div>
        </div>
        <div className="cockpit-bottom-list">
          <div className="item"><span className="ck">✓</span>Inclus dans ton abonnement</div>
          <div className="item"><span className="ck">✓</span>Paramétré par notre équipe en 7 jours</div>
          <div className="item"><span className="ck">✓</span>Agents IA formés à ton métier</div>
        </div>
      </div>
    </div>
  </section>
);

const BoutiqueSection = () => (
  <section id="boutique" className="section section-cream">
    <div className="container-narrow">
      <div className="section-head">
        <span className="eyebrow eyebrow-orange">Ta boutique e-commerce</span>
        <h2 className="t-h2">Vends directement tes produits.<br /><span className="accent">Garde 100% de tes revenus.</span></h2>
        <p className="t-lead">Tu vends, ton argent va directement sur ton compte comme une vraie boutique, mais en ligne.</p>
      </div>

      <div className="commission-banner" style={{ 
        background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)', 
        border: `2px solid ${C.orange}`,
        borderRadius: 18,
        padding: 24,
        marginBottom: 40
      }}>
        <div className="commission-title" style={{ color: C.navy }}>Ce que prennent les autres sur chaque vente</div>
        <div className="commission-row">
          <div className="comm-item bad"><div className="comm-logo">Amazon</div><div className="comm-pct red">15%</div><div className="comm-label">à 30%</div></div>
          <div className="comm-vs">vs</div>
          <div className="comm-item bad"><div className="comm-logo">ManoMano</div><div className="comm-pct red">15%</div><div className="comm-label">à 25%</div></div>
          <div className="comm-vs">vs</div>
          <div className="comm-item bad"><div className="comm-logo">Cdiscount</div><div className="comm-pct red">12%</div><div className="comm-label">à 20%</div></div>
          <div className="comm-arrow">→</div>
          <div className="comm-item good"><div className="comm-logo" style={{ color: C.green, fontWeight: 700 }}>AbracadaWorld</div><div className="comm-pct green">0%</div><div className="comm-label green">Pour toujours</div></div>
        </div>
      </div>

      <div className="boutique-grid">
        {/* Version Desktop */}
        <div className="hide-on-mobile">
          <div className="boutique-mockup-label">Aperçu — Ta boutique en ligne</div>
          <div className="boutique-mockup">
            <div className="boutique-header">
              <div className="boutique-avatar">🛠️</div>
              <div>
                <div className="boutique-name">Durand Outillage Pro</div>
                <div className="boutique-tagline">Outillage & Équipements BTP</div>
              </div>
              <div className="boutique-verified">✓ Certifié</div>
            </div>
            <div className="boutique-body">
              <div className="boutique-products">
                <div className="prod-card">
                  <div className="prod-img">🔩</div>
                  <div className="prod-name">Kit fixation inox 200 pcs</div>
                  <div className="prod-price">49€</div>
                  <div className="prod-badge">✓ En stock</div>
                </div>
                <div className="prod-card">
                  <div className="prod-img">🪛</div>
                  <div className="prod-name">Perceuse visseuse pro 18V</div>
                  <div className="prod-price">189€</div>
                  <div className="prod-badge">✓ Livraison 48h</div>
                </div>
              </div>
              <div className="via-box">
                <div className="via-hdr">
                  <div className="via-av">🤖</div>
                  <div>
                    <div className="via-name">Pierre — Vendeur IA</div>
                    <div className="via-sub">Agent de Durand Plomberie</div>
                  </div>
                  <div className="via-dot"></div>
                </div>
                <div className="via-msg user">Bonjour, mon évier fuit depuis ce matin…</div>
                <div className="via-msg">M. Durand est disponible demain 8h — 89€ TTC. Je vous réserve le créneau ?</div>
                <div className="via-coins">
                  <span className="via-coin-badge">⚡ AbracaCoins</span>
                  <span>Option · Formé par notre équipe</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Version Mobile Enrichie */}
        <div className="show-on-mobile-only">
          <div className="boutique-mockup-label">Aperçu — Ta boutique en ligne</div>
          <p style={{ fontSize: 14, color: C.gray600, marginBottom: 16, lineHeight: 1.5 }}>
            Produits, prestations, demandes et paiements : tout est réuni pour vendre sans intermédiaire.
          </p>
          <div className="boutique-mockup">
            <div className="boutique-header">
              <div className="boutique-avatar">🛠️</div>
              <div>
                <div className="boutique-name">Durand Outillage Pro</div>
                <div className="boutique-tagline">Outillage & Équipements BTP</div>
              </div>
              <div className="boutique-verified">✓ Certifié</div>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 12, 
              padding: '10px 16px',
              background: C.gray50,
              fontSize: 10,
              color: C.gray600,
              flexWrap: 'wrap',
            }}>
              <span>✓ Boutique vérifiée</span>
              <span>✓ Paiement sécurisé</span>
              <span>✓ Contact direct</span>
            </div>
            <div className="boutique-body">
              <div className="boutique-products">
                <div className="prod-card" style={{ paddingBottom: 12 }}>
                  <div className="prod-img">🔩</div>
                  <div className="prod-name">Kit fixation inox 200 pcs</div>
                  <div className="prod-price">49 €</div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 9, color: C.green, fontWeight: 600 }}>✓ En stock</span>
                    <span style={{ fontSize: 9, color: C.gray500 }}>· Livraison 48h</span>
                  </div>
                  <button style={{
                    marginTop: 10,
                    padding: '6px 16px',
                    background: C.orange,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>Acheter</button>
                </div>
                <div className="prod-card" style={{ paddingBottom: 12 }}>
                  <div className="prod-img">🪛</div>
                  <div className="prod-name">Perceuse visseuse pro 18V</div>
                  <div className="prod-price">189 €</div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 9, color: C.green, fontWeight: 600 }}>✓ Disponible</span>
                    <span style={{ fontSize: 9, color: C.gray500 }}>· Retrait ou livraison</span>
                  </div>
                  <button style={{
                    marginTop: 10,
                    padding: '6px 16px',
                    background: '#fff',
                    color: C.navy,
                    border: `1px solid ${C.navy}`,
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>Demander conseil</button>
                </div>
              </div>
              
              {/* Vendeur IA Mobile */}
              <div style={{
                margin: '16px',
                padding: '14px',
                background: 'linear-gradient(135deg, #FFF8F4 0%, #FFF 100%)',
                border: `1px solid ${C.orange}20`,
                borderRadius: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>🤖</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>Vendeur IA disponible 24h/24</span>
                </div>
                <p style={{ fontSize: 12, color: C.gray600, lineHeight: 1.5, margin: 0 }}>
                  Il renseigne les clients, conseille les bons produits et peut transformer une question en commande ou en demande de devis.
                </p>
              </div>

              {/* Catalogue évolutif */}
              <div style={{
                margin: '0 16px 16px',
                padding: '14px',
                background: C.gray50,
                borderRadius: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>📦</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>Catalogue évolutif</span>
                </div>
                <p style={{ fontSize: 12, color: C.gray600, lineHeight: 1.5, margin: 0 }}>
                  Commence avec tes premières offres, puis fais grandir ta boutique jusqu'à 100 000 produits dans les formules avancées.
                </p>
              </div>
            </div>
          </div>
          
          {/* Blocs mobile au-dessus de "Elle les vend" */}
          <div className="show-on-mobile-only" style={{ marginTop: 16, marginBottom: 16 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 12,
            }}>
              <div style={{
                background: '#fff',
                border: `1px solid ${C.gray200}`,
                borderRadius: 16,
                padding: 20,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
                <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: C.navy, marginBottom: 8 }}>0% de commission</div>
                <div style={{ fontSize: 13, color: C.gray600, lineHeight: 1.5 }}>Tu signes une prestation ou tu vends un produit, tu encaisses 100% du montant. AbracadaWorld ne prend rien sur tes ventes.</div>
              </div>
              <div style={{
                background: '#fff',
                border: `1px solid ${C.gray200}`,
                borderRadius: 16,
                padding: 20,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
                <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: C.navy, marginBottom: 8 }}>Contact direct client</div>
                <div style={{ fontSize: 13, color: C.gray600, lineHeight: 1.5 }}>Le client te parle directement dans ta boutique. Aucun intermédiaire.</div>
              </div>
            </div>
          </div>
          
          {/* Phrase forte de conclusion */}
          <div style={{
            marginTop: 0,
            padding: '16px 20px',
            background: 'linear-gradient(135deg, #FFF8F4 0%, #FFEEE4 100%)',
            border: `2px solid ${C.orange}`,
            borderRadius: 12,
            textAlign: 'center',
          }}>
            <p style={{ 
              margin: 0, 
              color: C.navy, 
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              lineHeight: 1.4,
            }}>
              Ta boutique ne montre pas seulement tes produits. <span style={{ color: C.orange }}>Elle les vend.</span>
            </p>
          </div>
        </div>

        <div className="boutique-feats hide-on-mobile">
          <div className="boutique-feat">
            <div className="ico">🏪</div>
            <div className="ttl">Ta boutique à ton nom</div>
            <div className="desc">Pas "une boutique sur une plateforme". Ta boutique, ton identité, tes couleurs, tes produits et tes prestations. Tes clients viennent chez toi.</div>
          </div>
          <div className="boutique-feat">
            <div className="ico">💬</div>
            <div className="ttl">Contact direct vendeur → acheteur</div>
            <div className="desc">Le client te parle directement. Pas de formulaire, pas d'intermédiaire. Comme dans une vraie boutique physique — tu connais ton client avant même qu'il commande.</div>
          </div>
          <div className="boutique-feat">
            <div className="ico">🤖</div>
            <div className="ttl">Vendeur IA dans ta boutique</div>
            <div className="desc">En option — notre équipe crée et forme un agent IA à tes produits et prestations. Il accueille, conseille et vend à ta place 24h/24. Fonctionne avec tes AbracaCoins.</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════════════
// ENCAISSEMENT SECTION — Paiements & Terminal NFC
// ══════════════════════════════════════════════════════════════════════════════
const EncaissementSection = () => (
  <section id="encaissement" className="section show-on-mobile-only" style={{ background: '#fff', paddingTop: 48, paddingBottom: 32 }}>
    <div className="container" style={{ padding: '0 20px' }}>
      
      {/* Header de section */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            borderRadius: 999,
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(22, 163, 74, 0.08))',
            border: '1px solid rgba(34, 197, 94, 0.25)',
            color: '#16a34a',
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}>
            <span style={{ fontSize: 14 }}>💰</span>
            Encaissement sécurisé
          </span>
        </div>
        <h2 style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: 'clamp(22px, 5vw, 28px)',
          color: C.navy,
          marginBottom: 10,
          lineHeight: 1.2,
        }}>
          Encaisse partout,<span style={{ color: C.orange }}> sans stress.</span>
        </h2>
        <p style={{
          fontSize: 14,
          color: C.gray600,
          lineHeight: 1.6,
          maxWidth: 480,
          margin: '0 auto',
        }}>
          Paiement sécurisé en ligne, ton propre module de paiement ou ton téléphone transformé en terminal — tu choisis comment encaisser.
        </p>
      </div>

      {/* Bloc 1 : Compte Séquestre - ORIGINAL */}
      <div className="sequestre" style={{ marginBottom: 24 }}>
        <div className="ico">🔒</div>
        <div>
          <div className="ttl">Paiement sécurisé — Compte séquestre</div>
          <div className="desc">Le client paie avant la prestation ou le client paie à la commande. L'argent est conservé en sécurité et libéré automatiquement dès le produit réceptionné ou dès la prestation faite. Une garantie totale pour l'acheteur et pour le vendeur.</div>
          <div className="badges badges-mobile">
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
              <span className="badge">✓ Acheteur protégé</span>
              <span className="badge">✓ Vendeur payé</span>
              <span className="badge">✓ Zéro impayé</span>
            </div>
          </div>
          
          <div style={{ 
            marginTop: 16, 
            display: 'flex', 
            justifyContent: 'center',
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
            }}>
              Boutique virtuelle dans AbracadaWorld
            </span>
          </div>
        </div>
      </div>

      {/* Bloc 2 : Module de paiement personnel - ORIGINAL */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          background: '#fff',
          border: `2px solid ${C.orange}`,
          borderRadius: 16,
          padding: 20,
          position: 'relative',
        }}>
          <span style={{
            position: 'absolute',
            top: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 8,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
          }}>Formule Premium</span>
          
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>💳</div>
            <div style={{ 
              fontFamily: "'Plus Jakarta Sans', sans-serif", 
              fontWeight: 700, 
              fontSize: 16, 
              color: C.navy, 
              marginBottom: 10 
            }}>
              Ton propre module de paiement
            </div>
            <p style={{ 
              fontSize: 13, 
              color: C.gray600, 
              lineHeight: 1.5, 
              margin: 0 
            }}>
              Avec la formule Premium, tu obtiens ton propre site dédié. Tu peux y connecter ton propre module de paiement : paiement en plusieurs fois (Alma), ta banque, ou tout autre système de ton choix — et encaisser directement sur ton compte pro.
            </p>
            <div style={{ 
              marginTop: 12,
              display: 'flex', 
              justifyContent: 'center',
            }}>
              <span style={{
                background: C.navy,
                color: '#fff',
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
              }}>
                Site dédié à ton nom
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bloc 3 : Terminal NFC - ORIGINAL */}
      <div style={{
        background: 'linear-gradient(135deg, #FFF8F4 0%, #FFEEE4 100%)',
        border: `2px solid ${C.orange}`,
        borderRadius: 16,
        padding: 24,
        paddingTop: 32,
        position: 'relative',
        overflow: 'visible',
        marginTop: 20,
      }}>
        <div style={{
          position: 'absolute',
          top: -14,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          padding: '6px 14px',
          borderRadius: 20,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 15px rgba(249,115,22,0.4)',
          animation: 'badgePulse 2s ease-in-out infinite',
        }}>
          ✨ Formule Pro Business Premium
        </div>
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: 16 
          }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              background: 'linear-gradient(135deg, #F97316, #EA580C)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(249,115,22,0.4)',
              animation: 'phoneFloat 3s ease-in-out infinite',
            }}>
              <span style={{ fontSize: 36 }}>📱</span>
            </div>
          </div>
          
          <div style={{ 
            fontFamily: "'Plus Jakarta Sans', sans-serif", 
            fontWeight: 800, 
            fontSize: 20, 
            color: C.navy, 
            marginBottom: 12,
            textAlign: 'center',
            lineHeight: 1.3,
          }}>
            Ton téléphone devient<br/>
            <span style={{ 
              color: C.orange,
              position: 'relative',
              display: 'inline-block',
            }}>
              terminal de paiement
              <span style={{
                position: 'absolute',
                bottom: -2,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, transparent, #F97316, transparent)',
                borderRadius: 2,
              }} />
            </span>
          </div>
          
          <p style={{ 
            fontSize: 14, 
            color: C.gray600, 
            lineHeight: 1.6, 
            margin: '0 0 16px 0',
            textAlign: 'center',
          }}>
            Sur un chantier, en livraison, en face à face — ton client sort sa carte ou son téléphone et paie en sans contact. Tu encaisses instantanément.
          </p>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 10,
            marginBottom: 16,
          }}>
            {[
              { icon: '📍', text: 'Encaisse partout — chantier, livraison, RDV' },
              { icon: '💳', text: 'Carte bancaire & Apple Pay / Google Pay' },
              { icon: '⚡', text: 'Paiement reçu instantanément' },
              { icon: '🔒', text: 'Sécurisé par notre partenaire certifié' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'rgba(249,115,22,0.08)',
                padding: '10px 14px',
                borderRadius: 10,
                border: '1px solid rgba(249,115,22,0.15)',
              }}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span style={{ 
                  fontSize: 13, 
                  color: C.navy,
                  fontWeight: 500,
                }}>{item.text}</span>
              </div>
            ))}
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
          }}>
            <span style={{
              background: C.navy,
              color: '#fff',
              padding: '8px 16px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span style={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                background: C.orange,
              }} />
              Activable depuis ton Cockpit
            </span>
          </div>
        </div>
        
        <style>{`
          @keyframes badgePulse {
            0%, 100% { transform: translateX(-50%) scale(1); box-shadow: 0 4px 15px rgba(249,115,22,0.4); }
            50% { transform: translateX(-50%) scale(1.05); box-shadow: 0 6px 25px rgba(249,115,22,0.6); }
          }
          @keyframes phoneFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-6px); }
          }
        `}</style>
      </div>
      
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════════════
// BOUTIQUE DESKTOP FEATURES — Section séquestre pour desktop
// ══════════════════════════════════════════════════════════════════════════════
const BoutiqueSectionDesktopContinuation = () => (
  <section className="section section-cream hide-on-mobile">
    <div className="container-narrow">
      <div className="sequestre">
        <div className="ico">🔒</div>
        <div>
          <div className="ttl">Paiement sécurisé — Compte séquestre</div>
          <div className="desc">Le client paie avant la prestation ou le client paie à la commande. L'argent est conservé en sécurité et libéré automatiquement dès le produit réceptionné ou dès la prestation faite. Une garantie totale pour l'acheteur et pour le vendeur.</div>
          <div className="badges badges-desktop">
            <span className="badge">✓ Acheteur protégé</span>
            <span className="badge">✓ Vendeur payé</span>
            <span className="badge">✓ Zéro impayé</span>
            <span className="badge">✓ Argent libéré à la validation</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════════════
// ENCAISSEMENT SECTION DESKTOP — Visible uniquement sur Desktop
// ══════════════════════════════════════════════════════════════════════════════
const EncaissementSectionDesktop = () => (
  <section id="encaissement-desktop" className="section hide-on-mobile" style={{ background: '#fff', paddingTop: 80, paddingBottom: 80 }}>
    <div className="container-narrow">
      
      {/* Header de section */}
      <div className="section-head" style={{ marginBottom: 48 }}>
        <span className="eyebrow eyebrow-green" style={{ 
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(22, 163, 74, 0.08))',
          border: '1px solid rgba(34, 197, 94, 0.25)',
          color: '#16a34a',
        }}>
          <span className="dot" style={{ background: '#22C55E' }} />
          Encaissement sécurisé
        </span>
        <h2 className="t-h2" style={{ marginTop: 20 }}>
          Encaisse partout,<span className="accent"> sans stress.</span>
        </h2>
        <p className="t-lead">
          Paiement sécurisé en ligne, ton propre module de paiement ou ton téléphone transformé en terminal — tu choisis comment encaisser.
        </p>
      </div>

      {/* Grille des 3 blocs */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: 24,
        marginBottom: 40,
      }}>
        
        {/* Bloc 1 : Compte Séquestre */}
        <div style={{
          background: 'rgba(34,197,94,0.06)',
          border: '1.5px solid rgba(34,197,94,0.3)',
          borderRadius: 18,
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>🔒</div>
          <div style={{ 
            fontFamily: "'Plus Jakarta Sans', sans-serif", 
            fontWeight: 700, 
            fontSize: 18, 
            color: '#16a34a',
            marginBottom: 12,
          }}>
            Paiement sécurisé — Compte séquestre
          </div>
          <p style={{ 
            fontSize: 14, 
            color: C.gray600, 
            lineHeight: 1.6, 
            marginBottom: 16,
            flex: 1,
          }}>
            Le client paie avant la prestation ou à la commande. L'argent est conservé en sécurité et libéré automatiquement dès le produit réceptionné ou la prestation faite.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <span style={{ 
              background: '#fff', 
              border: '1px solid rgba(34,197,94,0.3)', 
              color: '#16a34a', 
              padding: '4px 10px', 
              borderRadius: 999, 
              fontSize: 11, 
              fontWeight: 600 
            }}>✓ Acheteur protégé</span>
            <span style={{ 
              background: '#fff', 
              border: '1px solid rgba(34,197,94,0.3)', 
              color: '#16a34a', 
              padding: '4px 10px', 
              borderRadius: 999, 
              fontSize: 11, 
              fontWeight: 600 
            }}>✓ Vendeur payé</span>
            <span style={{ 
              background: '#fff', 
              border: '1px solid rgba(34,197,94,0.3)', 
              color: '#16a34a', 
              padding: '4px 10px', 
              borderRadius: 999, 
              fontSize: 11, 
              fontWeight: 600 
            }}>✓ Zéro impayé</span>
          </div>
          <div style={{ 
            marginTop: 16,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            textAlign: 'center',
          }}>
            Boutique virtuelle dans AbracadaWorld
          </div>
        </div>

        {/* Bloc 2 : Module de paiement personnel */}
        <div style={{
          background: '#fff',
          border: `2px solid ${C.orange}`,
          borderRadius: 18,
          padding: 28,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <span style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            padding: '5px 12px',
            borderRadius: 8,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
          }}>Formule Premium</span>
          
          <div style={{ fontSize: 36, marginBottom: 16, marginTop: 8 }}>💳</div>
          <div style={{ 
            fontFamily: "'Plus Jakarta Sans', sans-serif", 
            fontWeight: 700, 
            fontSize: 18, 
            color: C.navy,
            marginBottom: 12,
          }}>
            Ton propre module de paiement
          </div>
          <p style={{ 
            fontSize: 14, 
            color: C.gray600, 
            lineHeight: 1.6,
            marginBottom: 16,
            flex: 1,
          }}>
            Avec la formule Premium, tu obtiens ton propre site dédié. Tu peux y connecter ton propre module de paiement : paiement en plusieurs fois, ta banque, ou tout autre système de ton choix.
          </p>
          <div style={{ 
            background: C.navy,
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            textAlign: 'center',
          }}>
            Site dédié à ton nom
          </div>
        </div>

        {/* Bloc 3 : Terminal NFC */}
        <div style={{
          background: 'linear-gradient(135deg, #FFF8F4 0%, #FFEEE4 100%)',
          border: `2px solid ${C.orange}`,
          borderRadius: 18,
          padding: 28,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <span style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #E8650A, #ff7b1c)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            padding: '5px 12px',
            borderRadius: 8,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 15px rgba(249,115,22,0.4)',
          }}>Pro Business Premium</span>
          
          <div style={{ 
            width: 60,
            height: 60,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #F97316, #EA580C)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
            marginBottom: 16,
            marginTop: 8,
          }}>
            <span style={{ fontSize: 28 }}>📱</span>
          </div>
          <div style={{ 
            fontFamily: "'Plus Jakarta Sans', sans-serif", 
            fontWeight: 700, 
            fontSize: 18, 
            color: C.navy,
            marginBottom: 12,
          }}>
            Ton téléphone devient<br/>
            <span style={{ color: C.orange }}>terminal de paiement</span>
          </div>
          <p style={{ 
            fontSize: 14, 
            color: C.gray600, 
            lineHeight: 1.6,
            marginBottom: 16,
            flex: 1,
          }}>
            Sur un chantier, en livraison, en face à face — ton client sort sa carte ou son téléphone et paie en sans contact. Tu encaisses instantanément.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { icon: '📍', text: 'Encaisse partout — chantier, livraison, RDV' },
              { icon: '💳', text: 'Carte bancaire & Apple Pay / Google Pay' },
              { icon: '⚡', text: 'Paiement reçu instantanément' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(249,115,22,0.08)',
                padding: '6px 10px',
                borderRadius: 8,
                border: '1px solid rgba(249,115,22,0.15)',
              }}>
                <span style={{ fontSize: 14 }}>{item.icon}</span>
                <span style={{ fontSize: 12, color: C.navy, fontWeight: 500 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
    </div>
  </section>
);

// Placeholder pour éviter les erreurs - cette section va être supprimée
const SequestereDesktopPlaceholder = () => null;

// ══════════════════════════════════════════════════════════════════════════════
// DUAL SECTIONS INTRO — Présentation des deux types de vente
// ══════════════════════════════════════════════════════════════════════════════
const DualSectionsIntro = () => (
  <section className="section-cream dual-sections-intro" style={{ padding: '48px 0' }}>
    <div className="container" style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
      <div className="dual-badge-hide-mobile" style={{ marginBottom: 24 }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          borderRadius: 999,
          background: 'rgba(249, 115, 22, 0.12)',
          border: '1px solid rgba(249, 115, 22, 0.25)',
          color: C.orange,
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange }} />
          Une plateforme, deux façons de vendre
        </span>
      </div>
      <h3 className="dual-title-hide-mobile" style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(24px, 3vw, 36px)',
        color: C.navy,
        marginBottom: 16,
        lineHeight: 1.2,
      }}>
        Prestations de services <span style={{ color: C.orange }}>ou</span> Produits
      </h3>
      <p className="dual-desc-hide-mobile" style={{
        fontSize: 'clamp(15px, 1.6vw, 17px)',
        color: C.gray600,
        lineHeight: 1.6,
        marginBottom: 32,
        maxWidth: 600,
        margin: '0 auto 32px',
      }}>
        Tu es artisan, prestataire, ou tu vends des produits ? On t'explique comment ça marche pour chacun. Scroll pour découvrir les deux.
      </p>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 20,
        flexWrap: 'wrap',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <h3 className="dual-title-mobile-only" style={{
          display: 'none',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: 20,
          color: C.navy,
          marginBottom: 8,
          textAlign: 'center',
        }}>
          Vente de prestations ou produits
        </h3>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 20,
          flexWrap: 'wrap',
        }}>
        <a href="#prestataires" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 24px',
          background: '#fff',
          border: `2px solid ${C.navy}`,
          borderRadius: 14,
          textDecoration: 'none',
          transition: 'transform 0.2s, box-shadow 0.2s',
          width: 300,
          boxSizing: 'border-box',
        }}>
          <span style={{ fontSize: 24, width: 30, textAlign: 'center' }}>🔧</span>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: C.navy }}>Prestations de services</div>
            <div style={{ fontSize: 12, color: C.gray500 }}>Moteur de recherche par métier, par ville, demande de devis, etc...</div>
          </div>
          <span style={{ color: C.navy, fontWeight: 700, width: 20, textAlign: 'center' }}>↓</span>
        </a>
        <a href="#boutique" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 24px',
          background: '#fff',
          border: `2px solid ${C.orange}`,
          borderRadius: 14,
          textDecoration: 'none',
          transition: 'transform 0.2s, box-shadow 0.2s',
          width: 300,
          boxSizing: 'border-box',
        }}>
          <span style={{ fontSize: 24, width: 30, textAlign: 'center' }}>📦</span>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: C.orange }}>Vente de produits</div>
            <div style={{ fontSize: 12, color: C.gray500 }}>Accessoires, matériaux, produits, etc...</div>
          </div>
          <span style={{ color: C.orange, fontWeight: 700, width: 20, textAlign: 'center' }}>↓</span>
        </a>
        </div>
      </div>
    </div>
  </section>
);

const PrestatairesSection = () => (
  <section id="prestataires" className="section section-cream">
    <div className="container">
      <div className="section-head">
        <span className="eyebrow eyebrow-orange">Prestataires de services</span>
        <h2 className="t-h2 prestataires-title-hide-mobile">Ta boutique. Tes clients.<br /><span className="accent">Zéro intermédiaire.</span></h2>
        <p className="t-lead">Un moteur de recherche pour te trouver, une boutique à ton nom pour te présenter, et des demandes de devis qui arrivent directement chez toi — sans payer le moindre lead.</p>
      </div>

      <div className="prest-grid">
        <div>
          <h3 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(20px, 2.5vw, 28px)',
            color: C.navy,
            marginBottom: 24,
            textAlign: 'center',
          }}>
            Deux façons de trouver des clients
          </h3>
          <div className="boutique-mockup-label">1. Le moteur de recherche AbracadaWorld</div>
          <div className="search-wrap">
            <div className="search-bar">
              <div className="search-icon">🔍</div>
              <div className="search-input">Rechercher un métier, une ville...</div>
            </div>
            <div className="search-filters">
              <div className="filter-chip active">🔧 Métier</div>
              <div className="filter-chip">📍 Localisation</div>
              <div className="filter-chip">🕐 Disponibilité</div>
            </div>
            <div className="artisan-cards">
              <div className="artisan-card">
                <div className="artisan-banner">
                  <div className="artisan-avatar">🔧</div>
                  <div className="artisan-verified">✓ Vérifié</div>
                </div>
                <div className="artisan-body">
                  <div className="artisan-name">Plomberie Martin & Fils</div>
                  <div className="artisan-meta">📍 Lyon (69003)</div>
                  <div className="artisan-stars">★ 4.9 <span className="count">(127 avis)</span></div>
                  <div className="artisan-tags">
                    <span className="artisan-tag">Dépannage urgent</span>
                    <span className="artisan-tag">Sanitaires</span>
                    <span className="artisan-tag">Rénovation</span>
                  </div>
                  <button className="artisan-btn">Voir le Plombier</button>
                </div>
              </div>
              <div className="artisan-card">
                <div className="artisan-banner" style={{ background: 'linear-gradient(135deg,#e8f4fd,#dbeafe)' }}>
                  <div className="artisan-avatar" style={{ background: C.navy }}>⚡</div>
                  <div className="artisan-verified">✓ Vérifié</div>
                </div>
                <div className="artisan-body">
                  <div className="artisan-name">Électricité Dupont Pro</div>
                  <div className="artisan-meta">📍 Paris (75011)</div>
                  <div className="artisan-stars">★ 4.8 <span className="count">(89 avis)</span></div>
                  <div className="artisan-tags">
                    <span className="artisan-tag">Mise aux normes</span>
                    <span className="artisan-tag">Dépannage</span>
                  </div>
                  <button className="artisan-btn">Voir l'Électricien</button>
                </div>
              </div>
            </div>
          </div>

          <div className="boutique-mockup-label" style={{ marginTop: 32 }}>2. Des demandes de devis</div>
          <div className="vs-block">
            <div className="vs-bad hide-on-mobile">
              <div className="ico">😤</div>
              <div>
                <div className="ttl">Travaux.com, Houzz… tu paies chaque lead</div>
                <div className="desc">Entre 30€ et 150€ par contact client — même si la prestation ne se fait pas. Sur 10 leads, tu en signes 2. Tu as payé pour 10.</div>
              </div>
            </div>
            <div className="vs-good">
              <div className="ico">🎯</div>
              <div>
                <div className="ttl">Zéro coût par contact.</div>
                <div className="desc">Les demandes de devis arrivent directement chez toi. Tu ne paies rien. Ni pour le contact, ni pour le devis, ni pour la mise en relation.</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="boutique-mockup-label">Comment fonctionnent les demandes de devis</div>
          <div className="devis-flow">
            <div className="kicker">Le parcours d'une demande</div>
            <div className="devis-steps">
              <div className="devis-step">
                <div className="ds-num">1</div>
                <div>
                  <div className="ds-title">Le particulier décrit son projet</div>
                  <div className="ds-desc">Il détaille son besoin, sa localisation, son budget indicatif et ses disponibilités — en quelques secondes depuis son téléphone.</div>
                </div>
              </div>
              <div className="devis-step">
                <div className="ds-num">2</div>
                <div>
                  <div className="ds-title">L'IA sélectionne les 5 meilleurs profils</div>
                  <div className="ds-desc">Elle analyse le métier, la zone géographique, les avis, la disponibilité et les spécialités de chaque prestataire pour choisir les plus pertinents.</div>
                  <span className="ds-tag">⚡ Sélection automatique en quelques secondes</span>
                </div>
              </div>
              <div className="devis-step">
                <div className="ds-num">3</div>
                <div>
                  <div className="ds-title">Tu reçois la demande directement</div>
                  <div className="ds-desc">Notification immédiate. Tu vois le projet, tu décides si tu réponds. Zéro obligation, zéro frais si tu passes.</div>
                </div>
              </div>
              <div className="devis-step">
                <div className="ds-num">4</div>
                <div>
                  <div className="ds-title">Ton Super Assistant IA ou ton Commercial IA rédigent le devis</div>
                  <div className="ds-desc">Tu parles, il chiffre, il rédige, il envoie. Le client reçoit ton devis professionnel en quelques minutes.</div>
                  <span className="ds-tag">✓ Zéro coût par contact — Zéro commission</span>
                </div>
              </div>
            </div>

            <div className="ia-selection hide-on-mobile">
              <div className="ia-sel-title">⚡ L'IA a sélectionné 5 artisans pour cette demande</div>
              <div className="ia-artisans">
                <div className="ia-artisan">
                  <div className="ia-av">🔧</div>
                  <div className="ia-name">Plomberie Martin & Fils</div>
                  <div className="ia-score">Lyon · ★ 4.9</div>
                  <div className="ia-match">98% match</div>
                </div>
                <div className="ia-artisan">
                  <div className="ia-av">🚿</div>
                  <div className="ia-name">Durand Plombier Pro</div>
                  <div className="ia-score">Lyon · ★ 4.7</div>
                  <div className="ia-match">94% match</div>
                </div>
                <div className="ia-artisan" style={{ opacity: 0.55 }}>
                  <div className="ia-av">🔩</div>
                  <div className="ia-name">+ 3 autres artisans</div>
                  <div className="ia-score">sélectionnés</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="prest-av-grid">
        {[
          { ic: '🏪', t: 'Ta boutique à ton nom', d: 'Tes prestations, tes tarifs, tes réalisations, tes avis. Une vraie vitrine pro — pas un profil anonyme sur un annuaire.', hideOnMobile: true },
          { ic: '🎯', t: 'Zéro coût par contact', d: "Les demandes de devis arrivent directement chez toi. Tu ne paies rien par contact. Jamais. C'est inclus dans ton abonnement.", hideOnMobile: true },
          { ic: '💰', t: '0% de commission', d: "Tu signes une prestation ou tu vends un produit, tu encaisses 100% du montant. AbracadaWorld ne prend rien sur tes ventes.", hideOnMobile: true },
          { ic: '💬', t: 'Contact direct client', d: "Le client te parle directement dans ta boutique. Aucun intermédiaire.", hideOnMobile: true },
          { ic: '⭐', t: 'Artisans 5 étoiles & du mois', d: "Ton travail est récompensé. Les meilleurs artisans sont mis en avant dans des classements visibles par tous les particuliers.", hideOnMobile: true },
          { ic: '🛡️', t: 'Profil certifié & vérifié', d: "Badge Vérifié sur ta fiche. Les clients savent qu'ils parlent à un vrai professionnel — pas un profil inventé.", hideOnMobile: true },
        ].map((it, i) => (
          <div key={i} className={`prest-av ${it.hideOnMobile ? 'hide-on-mobile' : ''}`}>
            <div className="ico">{it.ic}</div>
            <div className="ttl">{it.t}</div>
            <div className="desc">{it.d}</div>
          </div>
        ))}
      </div>

      <div className="social-seo hide-on-mobile">
        <div className="rs-card">
          <div className="kicker">📣 Réseau social certifié</div>
          <div className="head">L'unique réseau social<br />100% Pro.</div>
          <p className="desc">Tu postes une fois sur AbracadaWorld — ton <strong>Community Manager IA</strong> duplique automatiquement le contenu sur <strong>tous tes réseaux</strong> simultanément. Il répond aussi à tous les commentaires et tous les messages — sur chaque réseau — à ta place.</p>

          <div className="rs-post">
            <div className="rs-post-hdr">
              <div className="rs-post-av">🔧</div>
              <div>
                <div className="rs-post-name">Durand Plomberie</div>
                <div className="rs-post-pub">Publié sur AbracadaWorld</div>
              </div>
              <div className="rs-post-cert">✓ Certifié</div>
            </div>
            <div className="rs-post-body">Rénovation salle de bain terminée chez les Garcia 🚿✨ Avant/après — résultat au top !</div>
            <div className="rs-dup-lbl">⚡ Dupliqué automatiquement sur</div>
            <div className="rs-net-row">
              <div className="rs-net">📘 Facebook</div>
              <div className="rs-net">📸 Instagram</div>
              <div className="rs-net">🎵 TikTok</div>
              <div className="rs-net">💼 LinkedIn</div>
            </div>
          </div>

          <div className="cm-bloc">
            <div className="lbl">🤖 Community Manager IA — en action</div>
            <div className="row"><span className="ck">✓</span>Répond aux commentaires Facebook, Instagram, TikTok, LinkedIn</div>
            <div className="row"><span className="ck">✓</span>Gère tous les DMs sur chaque réseau — dans ton ton</div>
            <div className="row"><span className="ck">✓</span>Formé à ton métier, tes prestations et ta façon de parler</div>
          </div>
        </div>

        <div className="rs-card orange">
          <div className="kicker">🔎 Référencement local automatique</div>
          <div className="head">Visible sur Google<br />dans toute ta zone.</div>
          <p className="desc">Ton <strong>Agent Expert SEO</strong> génère automatiquement des pages optimisées pour chaque commune de ta zone d'intervention. Quand un client cherche sur Google, <strong>c'est toi qu'il trouve.</strong></p>
          <div style={{ position: 'relative' }}>
            <div className="rs-dup-lbl" style={{ color: 'rgba(255,255,255,0.7)' }}>Pages générées automatiquement</div>
            {[
              { n: 'Plombier Lyon 69003', u: 'abracadabati.com/plombier-lyon-69003' },
              { n: 'Plombier Villeurbanne', u: 'abracadabati.com/plombier-villeurbanne' },
              { n: 'Plombier Caluire-et-Cuire', u: 'abracadabati.com/plombier-caluire' },
            ].map((p, i) => (
              <div key={i} className="seo-page">
                <div className="seo-page-ic">🌐</div>
                <div>
                  <div className="seo-page-name">{p.n}</div>
                  <div className="seo-page-url">{p.u}</div>
                </div>
                <div className="seo-page-on">✓ En ligne</div>
              </div>
            ))}
            <div className="seo-summary">
              <div className="ttl">✓ Toutes tes communes en une fois</div>
              <div className="desc">Tu définis ta zone d'intervention. Ton Agent SEO génère une page optimisée pour chaque commune — automatiquement, sans que tu aies rien à faire.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Mobile-only Social Block (Réseau social)
const SocialMobile = () => (
  <section className="section show-on-mobile-only" style={{ paddingTop: 0 }}>
    <div className="container">
      {/* Header avec badge comme Équipe Agents IA */}
      <div className="section-head" style={{ marginBottom: 24 }}>
        <span className="eyebrow eyebrow-orange">Réseaux sociaux</span>
        <h2 className="t-h2" style={{ whiteSpace: 'nowrap', fontSize: 'clamp(22px, 5vw, 32px)' }}>Tous tes réseaux <span className="accent">interconnectés.</span></h2>
        <p className="t-lead">Gérer tes réseaux sociaux<br />n'a jamais été aussi facile.</p>
      </div>

      {/* Schéma visuel du flux */}
      <div style={{
        background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
        border: `2px solid ${C.orange}`,
        borderRadius: 20,
        padding: 20,
        marginBottom: 24,
      }}>
        {/* Étape 1: Poste sur AbracadaWorld */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            background: 'linear-gradient(180deg, #F0F4F8 0%, #E1E8F0 100%)',
            border: `2px solid ${C.navy}`,
            color: C.navy,
            padding: '10px 16px',
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 14,
          }}>
            <span>📝</span> Tu postes sur AbracadaWorld
          </div>
        </div>

        {/* Flèche */}
        <div style={{ textAlign: 'center', color: C.orange, fontSize: 20, marginBottom: 12 }}>▼</div>

        {/* Duplication automatique */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, marginBottom: 6, textAlign: 'center' }}>
            ⚡ Dupliqué automatiquement sur
          </div>
          {/* 4 petites flèches */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 8 }}>
            <div style={{ width: 44, textAlign: 'center', color: C.orange, fontSize: 14 }}>▼</div>
            <div style={{ width: 44, textAlign: 'center', color: C.orange, fontSize: 14 }}>▼</div>
            <div style={{ width: 44, textAlign: 'center', color: C.orange, fontSize: 14 }}>▼</div>
            <div style={{ width: 44, textAlign: 'center', color: C.orange, fontSize: 14 }}>▼</div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            {/* Facebook */}
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: '#1877F2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </div>
            {/* Instagram */}
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </div>
            {/* TikTok */}
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </div>
            {/* LinkedIn */}
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: '#0A66C2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                <rect x="2" y="9" width="4" height="12"/>
                <circle cx="4" cy="4" r="2"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Flèches */}
        <div style={{ textAlign: 'center', color: C.orange, fontSize: 20, marginBottom: 12 }}>▼</div>

        {/* Formule Gratuite ou Pro */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          border: `2px solid ${C.navy}`,
        }}>
          <div style={{ 
            fontSize: 11, 
            fontWeight: 700, 
            color: C.navy, 
            marginBottom: 10, 
            textAlign: 'center',
            background: 'linear-gradient(180deg, #F0F4F8 0%, #E1E8F0 100%)',
            border: `2px solid ${C.navy}`,
            padding: '6px 12px',
            borderRadius: 6,
            display: 'inline-block',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            📱 Formule Gratuite ou Pro
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 8, textAlign: 'center' }}>
            Tu gères tout depuis un seul endroit
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              '✓ Réponds aux commentaires',
              '✓ Gère tous tes DMs',
              '✓ Un seul compte pour tout',
            ].map((item, i) => (
              <div key={i} style={{ fontSize: 12, color: C.gray700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: C.navy }}>{item.substring(0, 1)}</span>
                <span>{item.substring(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Flèches */}
        <div style={{ textAlign: 'center', color: C.orange, fontSize: 20, marginBottom: 12 }}>▼</div>

        {/* Formule Business ou Premium - Gestion automatique */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 14,
          border: `2px solid ${C.orange}`,
          animation: 'pulseGlow 2s ease-in-out infinite',
        }}>
          <style>{`
            @keyframes pulseGlow {
              0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.4); }
              50% { box-shadow: 0 0 20px 4px rgba(249,115,22,0.3); }
            }
          `}</style>
          <div style={{ 
            fontSize: 11, 
            fontWeight: 700, 
            color: C.orange, 
            marginBottom: 10, 
            textAlign: 'center',
            background: 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)',
            border: `2px solid ${C.orange}`,
            padding: '6px 12px',
            borderRadius: 6,
            display: 'inline-block',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            🤖 Formule Business ou Premium
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 8, textAlign: 'center' }}>
            Ton Community Manager IA gère tout
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              '✓ Répond aux commentaires automatiquement',
              '✓ Gère tous les DMs à ta place',
              '✓ Adapté à ton ton et ton métier',
            ].map((item, i) => (
              <div key={i} style={{ fontSize: 12, color: C.gray700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: C.green }}>{item.substring(0, 1)}</span>
                <span>{item.substring(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bloc AbracadaWorld unique */}
      <div style={{
        background: `linear-gradient(135deg, ${C.navy} 0%, #3d5a80 100%)`,
        borderRadius: 20,
        padding: 24,
        marginBottom: 24,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(30,58,95,0.3)',
      }}>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 0.8; }
          }
        `}</style>
        {/* Cercles décoratifs */}
        <div style={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          animation: 'pulse 4s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute',
          bottom: -20,
          left: -20,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          animation: 'pulse 3s ease-in-out infinite 1s',
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            fontSize: 12, 
            fontWeight: 700, 
            color: C.orange, 
            marginBottom: 12,
            letterSpacing: '0.5px',
          }}>
            ✦ LE CONCEPT ABRACADAWORLD
          </div>
          <div style={{ 
            fontSize: 15, 
            fontWeight: 600, 
            color: '#fff',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            Un réseau bienveillant, thématique.<br />
            Où chaque personne est unique.
          </div>
          <div style={{ 
            fontSize: 'clamp(14px, 4vw, 18px)',
            fontWeight: 800, 
            color: '#fff',
            marginBottom: 16,
            whiteSpace: 'nowrap',
            textAlign: 'center',
            background: 'rgba(255,255,255,0.15)',
            padding: '12px 16px',
            borderRadius: 12,
            animation: 'float 3s ease-in-out infinite',
          }}>
            1 personne = 1 compte = 0 faux profil
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['Bienveillant', 'Utile', 'Thématique'].map((tag, i) => (
              <span key={i} style={{
                background: 'rgba(249,115,22,0.25)',
                color: C.orange,
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 700,
              }}>{tag}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Bloc réseau social thématique */}
      <div className="social-seo" style={{ marginTop: 0 }}>
        <div className="rs-card" style={{
          background: '#fff',
          border: `2px solid ${C.orange}`,
        }}>
          <div className="kicker" style={{
            background: C.orange,
            color: '#fff',
          }}>📣 Réseau social thématique</div>
          <div className="head" style={{ color: C.navy }}>L'unique réseau social 100% Pro.</div>
          <p className="desc">Tu postes une fois sur AbracadaWorld — ton <strong>Community Manager IA</strong> duplique automatiquement le contenu sur <strong>tous tes réseaux</strong> simultanément. Il répond aussi à tous les commentaires et tous les messages — sur chaque réseau — à ta place.</p>

          <div className="rs-post">
            <div className="rs-post-hdr">
              <div className="rs-post-av">🔧</div>
              <div>
                <div className="rs-post-name">Durand Plomberie</div>
                <div className="rs-post-pub">Publié sur AbracadaWorld</div>
              </div>
              <div className="rs-post-cert">✓ Certifié</div>
            </div>
            <div className="rs-post-body">Rénovation salle de bain terminée chez les Garcia 🚿✨ Avant/après — résultat au top !</div>
            <div className="rs-dup-lbl">⚡ Dupliqué automatiquement sur</div>
            <div className="rs-net-row">
              <div className="rs-net">📘 Facebook</div>
              <div className="rs-net">📸 Instagram</div>
              <div className="rs-net">🎵 TikTok</div>
              <div className="rs-net">💼 LinkedIn</div>
            </div>
          </div>

          <div className="cm-bloc">
            <div className="lbl">🤖 Community Manager IA — en action</div>
            <div className="row"><span className="ck">✓</span>Répond aux commentaires Facebook, Instagram, TikTok, LinkedIn</div>
            <div className="row"><span className="ck">✓</span>Gère tous les DMs sur chaque réseau — dans ton ton</div>
            <div className="row"><span className="ck">✓</span>Formé à ton métier, tes prestations et ta façon de parler</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Mobile-only SEO Block (Visible sur Google) - placed between module paiement and Centre de pilotage
const SeoMobile = () => (
  <section className="section show-on-mobile-only" style={{ paddingTop: 0 }}>
    <div className="container">
      <div className="social-seo" style={{ marginTop: 0 }}>
        <div className="rs-card orange">
          <div className="kicker">🔎 Référencement local automatique</div>
          <div className="head">Visible sur Google<br />dans toute ta zone.</div>
          <p className="desc">Ton <strong>Agent Expert SEO</strong> génère automatiquement des pages optimisées pour chaque commune de ta zone d'intervention. Quand un client cherche sur Google, <strong>c'est toi qu'il trouve.</strong></p>
          <div style={{ position: 'relative' }}>
            <div className="rs-dup-lbl" style={{ color: 'rgba(255,255,255,0.7)' }}>Pages générées automatiquement</div>
            {[
              { n: 'Plombier Lyon 69003', u: 'abracadaworld.com/plombier-lyon-69003' },
              { n: 'Plombier Villeurbanne', u: 'abracadaworld.com/plombier-villeurbanne' },
              { n: 'Plombier Caluire-et-Cuire', u: 'abracadaworld.com/plombier-caluire' },
            ].map((p, i) => (
              <div key={i} className="seo-page">
                <div className="seo-page-ic">🌐</div>
                <div>
                  <div className="seo-page-name">{p.n}</div>
                  <div className="seo-page-url">{p.u}</div>
                </div>
                <div className="seo-page-on">✓ En ligne</div>
              </div>
            ))}
            <div className="seo-summary">
              <div className="ttl">✓ Toutes tes communes en une fois</div>
              <div className="desc">Tu définis ta zone d'intervention. Ton Agent SEO génère une page optimisée pour chaque commune — automatiquement, sans que tu aies rien à faire.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Mobile-only 7 Days Setup Section
const Setup7DaysMobile = () => (
  <section className="section show-on-mobile-only" style={{ paddingTop: 0 }}>
    <div className="container">
      <div className="section-head" style={{ marginBottom: 24 }}>
        <span className="eyebrow eyebrow-green">Livraison express</span>
        <h2 className="t-h2">En 7 jours,<br /><span className="accent">ton business est prêt.</span></h2>
        <p className="t-lead">Notre équipe configure<br />absolument tout pour toi.</p>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}>
        {[
          { num: 1, t: 'Ta boutique virtuelle', d: 'Prestations, produits, tarifs — en ligne.' },
          { num: 2, t: 'Ton site vitrine', d: 'Landing pages SEO générées automatiquement.' },
          { num: 3, t: 'Ton Super Assistant', d: 'Formé à ton métier, opérationnel jour 1.' },
          { num: 4, t: 'Ton Vendeur IA', d: 'Formé à tes produits pour vendre à ta place.' },
          { num: 5, t: 'Tes devis & factures', d: 'Modèles configurés à ton image.' },
          { num: 6, t: 'Tes réseaux connectés', d: 'Un post chez nous = publié partout.' },
          { num: 7, t: 'Ton catalogue organisé', d: 'Stock, prix, photos — tout en ordre.' },
        ].map((item, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '14px',
            background: '#fff',
            border: `1px solid ${C.gray200}`,
            borderRadius: 12,
          }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${C.orange}, ${C.orangeDeep})`,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 14,
              flexShrink: 0,
            }}>
              {item.num}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 2 }}>{item.t}</div>
              <div style={{ fontSize: 12, color: C.gray600, lineHeight: 1.4 }}>{item.d}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 20,
        padding: '16px',
        background: 'linear-gradient(180deg, #F0FDF4 0%, #DCFCE7 100%)',
        border: `2px solid ${C.green}`,
        borderRadius: 12,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 24, marginBottom: 6 }}>⏱</div>
        <div style={{ fontWeight: 800, fontSize: 16, color: C.green }}>Livraison clé en main en 7 jours</div>
        <div style={{ fontSize: 12, color: C.gray600, marginTop: 4 }}>Tu arrives, tu travailles. C'est tout.</div>
      </div>
    </div>
  </section>
);

// Mobile-only Pricing Section (Pas de porte + Formules)
const PricingMobile = () => (
  <section className="section show-on-mobile-only" style={{ paddingTop: 0, paddingBottom: 40 }}>
    <div className="container">
      <div style={{ textAlign: 'center', marginBottom: 0 }}>
        <span className="eyebrow eyebrow-orange">Prix du paramétrage</span>
        <h2 className="t-h2" style={{ fontSize: 22, marginTop: 8, marginBottom: 0 }}>En 7 jours, <span className="accent">ton business est prêt.</span></h2>
      </div>
      
      {/* Offre membre fondateur */}
      <div style={{
        padding: '14px 20px',
        background: 'rgba(34,197,94,0.1)',
        border: `2px solid ${C.green}`,
        borderRadius: 12,
        textAlign: 'center',
        marginTop: 16,
        marginBottom: 10,
        boxShadow: '0 4px 15px rgba(34,197,94,0.15)',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 8 }}>👑 Offre membre fondateur</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.navy, lineHeight: 1.6, marginBottom: 10 }}>
          ✓ -50% sur le pas de porte<br />
          ✓ -50% à vie sur la location<br />
          ✓ Paramétrage et formation inclus
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.orange, marginBottom: 12 }}>
          🎁 Pour les 200 premiers inscrits
        </div>
        <a 
          href="#tarifs"
          style={{
            display: 'inline-block',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: '#fff',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
            boxShadow: '0 8px 20px rgba(34,197,94,0.4)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          }}
          onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 12px 28px rgba(34,197,94,0.5)'; }}
          onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 8px 20px rgba(34,197,94,0.4)'; }}
        >
          Voir les offres fondateurs →
        </a>
      </div>
      
      {/* Flèche animée vers Pas de porte */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4, paddingBottom: 18 }}>
        <style>{`
          @keyframes bounceArrowDown {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(8px); }
          }
        `}</style>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: C.orange,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'bounceArrowDown 1.5s ease-in-out infinite',
          boxShadow: '0 4px 15px rgba(249,115,22,0.4)',
        }}>
          <span style={{ color: '#fff', fontSize: 18 }}>↓</span>
        </div>
      </div>

      {/* Pas de porte */}
      <div style={{
        background: '#fff',
        border: `2px solid ${C.navy}`,
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
      }}>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: C.orange,
          marginBottom: 12,
          textAlign: 'center',
        }}>
          💼 PAS DE PORTE — Paramétrage clé en main
        </div>
        
        <div style={{
          marginBottom: 12,
          padding: '8px 14px',
          background: 'rgba(34,197,94,0.1)',
          border: `1px solid ${C.green}`,
          borderRadius: 8,
          fontSize: 11,
          color: C.navy,
          textAlign: 'center',
          fontWeight: 600,
        }}>
          🎁 -50% réservé aux 200 premiers fondateurs
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { name: 'Formule Pro', oldPrice: '500€', newPrice: '250€' },
            { name: 'Formule Business', oldPrice: '1 000€', newPrice: '500€', popular: true },
            { name: 'Formule Premium', oldPrice: '1 500€', newPrice: '750€' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              background: item.popular ? 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)' : C.gray50,
              border: item.popular ? `2px solid ${C.orange}` : `1px solid ${C.gray200}`,
              borderRadius: 10,
              position: 'relative',
            }}>
              {item.popular && (
                <div style={{
                  position: 'absolute',
                  top: -8,
                  right: 10,
                  background: C.orange,
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 4,
                }}>POPULAIRE</div>
              )}
              <div style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{item.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ 
                  textDecoration: 'line-through', 
                  color: C.gray400, 
                  fontSize: 13 
                }}>{item.oldPrice}</span>
                <span style={{ 
                  fontWeight: 800, 
                  fontSize: 18, 
                  color: C.orange 
                }}>{item.newPrice}</span>
              </div>
            </div>
          ))}
        </div>
        
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <a 
            href="#tarifs" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 20px',
              background: C.navy,
              color: '#fff',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              textDecoration: 'none',
            }}
          >
            En savoir plus →
          </a>
        </div>
      </div>

      {/* Section Tarif - Formule sans surprise */}
      <div style={{ textAlign: 'center', marginTop: 32, marginBottom: 20 }}>
        <span className="eyebrow eyebrow-orange">Tarif sans surprise</span>
        <h2 className="t-h2" style={{ fontSize: 22, marginTop: 8, marginBottom: 6 }}>Choisis la formule<br /><span className="accent">qui te ressemble.</span></h2>
        <p style={{ fontSize: 13, color: C.gray600, marginTop: 8 }}>Démarre gratuitement, lance ta boutique virtuelle. Puis déploie les supers pouvoirs de ton assistant, de tes agents IA selon ta formule.</p>
      </div>

      {/* Formules mensuelles */}
      <div style={{
        background: '#fff',
        border: `2px solid ${C.orange}`,
        borderRadius: 16,
        padding: 20,
        marginBottom: 24,
      }}>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: C.navy,
          marginBottom: 16,
          textAlign: 'center',
        }}>
          🏪 LOCATION BOUTIQUE — Loyer mensuel
        </div>
        
        <div style={{
          background: 'rgba(34,197,94,0.1)',
          border: `1px solid ${C.green}`,
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 12,
          textAlign: 'center',
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.navy }}>🎁 -50% à vie réservé aux 200 premiers fondateurs</span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { name: 'Gratuite', price: '0€', desc: 'Pour découvrir' },
            { name: 'Pro', oldPrice: '50€', price: '25€', desc: '/mois', founder: true },
            { name: 'Business', oldPrice: '100€', price: '50€', desc: '/mois', popular: true, founder: true },
            { name: 'Premium', oldPrice: '150€', price: '75€', desc: '/mois', founder: true },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: item.popular ? 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)' : '#fff',
              border: item.popular ? `2px solid ${C.orange}` : `1px solid ${C.gray200}`,
              borderRadius: 10,
              position: 'relative',
            }}>
              {item.popular && (
                <div style={{
                  position: 'absolute',
                  top: -8,
                  right: 10,
                  background: C.orange,
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 4,
                }}>POPULAIRE</div>
              )}
              <div style={{ fontWeight: 600, fontSize: 14, color: C.navy }}>
                Formule {item.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                {item.oldPrice && (
                  <span style={{ textDecoration: 'line-through', color: C.gray400, fontSize: 13 }}>{item.oldPrice}</span>
                )}
                <span style={{ fontWeight: 800, fontSize: 20, color: C.orange }}>{item.price}</span>
                <span style={{ fontSize: 11, color: C.gray500 }}>{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
        
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <a 
            href="#tarifs" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 24px',
              background: C.orange,
              color: '#fff',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'none',
              boxShadow: '0 4px 14px rgba(249,115,22,0.3)',
            }}
          >
            Voir les formules →
          </a>
        </div>
      </div>

    </div>
  </section>
);

// Desktop Pricing Section (Prix du paramétrage + Tarifs sans surprise)
const PricingDesktop = () => (
  <section className="section hide-on-mobile" style={{ paddingTop: 60, paddingBottom: 60, background: '#fff' }}>
    <div className="container-narrow">
      
      {/* Header - Prix du paramétrage */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <span className="eyebrow eyebrow-orange" style={{ 
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12), rgba(234, 88, 12, 0.08))',
          border: '1px solid rgba(249, 115, 22, 0.25)',
          color: '#EA580C',
        }}>
          <span className="dot" style={{ background: '#F97316' }} />
          Prix du paramétrage
        </span>
        <h2 className="t-h2" style={{ marginTop: 20 }}>
          En 7 jours, <span className="accent">ton business est prêt.</span>
        </h2>
      </div>

      {/* Offre membre fondateur */}
      <div style={{
        padding: '28px 40px',
        background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(22,163,74,0.04) 100%)',
        border: `2px solid ${C.green}`,
        borderRadius: 20,
        textAlign: 'center',
        marginBottom: 40,
        boxShadow: '0 8px 30px rgba(34,197,94,0.12)',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 16 }}>👑 Offre membre fondateur</div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 32, 
          flexWrap: 'wrap',
          marginBottom: 20 
        }}>
          {[
            '✓ -50% sur le pas de porte',
            '✓ -50% à vie sur la location',
            '✓ Paramétrage et formation inclus',
          ].map((item, i) => (
            <div key={i} style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: C.navy,
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.7)',
              borderRadius: 8,
            }}>{item}</div>
          ))}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.orange, marginBottom: 20 }}>
          🎁 Pour les 200 premiers inscrits
        </div>
        <a 
          href="#tarifs"
          style={{
            display: 'inline-block',
            padding: '14px 32px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: '#fff',
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 15,
            textDecoration: 'none',
            boxShadow: '0 8px 24px rgba(34,197,94,0.4)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          }}
          onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 12px 32px rgba(34,197,94,0.5)'; }}
          onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 8px 24px rgba(34,197,94,0.4)'; }}
        >
          Voir les offres fondateurs →
        </a>
      </div>

      {/* Grille 2 colonnes : Pas de porte + Abonnements */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 32, marginBottom: 40 }}>
        
        {/* Pas de porte */}
        <div style={{
          background: '#fff',
          border: `2px solid ${C.navy}`,
          borderRadius: 20,
          padding: 28,
        }}>
          <div style={{
            fontSize: 15,
            fontWeight: 700,
            color: C.orange,
            marginBottom: 16,
            textAlign: 'center',
          }}>
            💼 PAS DE PORTE — Paramétrage clé en main
          </div>
          
          <div style={{
            marginBottom: 16,
            padding: '10px 16px',
            background: 'rgba(34,197,94,0.1)',
            border: `1px solid ${C.green}`,
            borderRadius: 10,
            fontSize: 12,
            color: C.navy,
            textAlign: 'center',
            fontWeight: 600,
          }}>
            🎁 -50% réservé aux 200 premiers fondateurs
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { name: 'Formule Pro', oldPrice: '500€', newPrice: '250€' },
              { name: 'Formule Business', oldPrice: '1 000€', newPrice: '500€', popular: true },
              { name: 'Formule Premium', oldPrice: '1 500€', newPrice: '750€' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                background: item.popular ? 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)' : C.gray50,
                border: item.popular ? `2px solid ${C.orange}` : `1px solid ${C.gray200}`,
                borderRadius: 12,
                position: 'relative',
              }}>
                {item.popular && (
                  <div style={{
                    position: 'absolute',
                    top: -10,
                    right: 12,
                    background: C.orange,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 6,
                  }}>POPULAIRE</div>
                )}
                <div style={{ fontWeight: 700, fontSize: 15, color: C.navy }}>{item.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ textDecoration: 'line-through', color: C.gray400, fontSize: 14 }}>{item.oldPrice}</span>
                  <span style={{ fontWeight: 800, fontSize: 22, color: C.orange }}>{item.newPrice}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Abonnements mensuels */}
        <div style={{
          background: '#fff',
          border: `2px solid ${C.orange}`,
          borderRadius: 20,
          padding: 28,
        }}>
          <div style={{
            fontSize: 15,
            fontWeight: 700,
            color: C.navy,
            marginBottom: 16,
            textAlign: 'center',
          }}>
            🏪 LOCATION BOUTIQUE — Loyer mensuel
          </div>
          
          <div style={{
            background: 'rgba(34,197,94,0.1)',
            border: `1px solid ${C.green}`,
            borderRadius: 10,
            padding: '10px 16px',
            marginBottom: 16,
            textAlign: 'center',
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>🎁 -50% à vie réservé aux 200 premiers fondateurs</span>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { name: 'Gratuite', price: '0€', desc: 'Pour découvrir' },
              { name: 'Pro', oldPrice: '50€', price: '25€', desc: '/mois', founder: true },
              { name: 'Business', oldPrice: '100€', price: '50€', desc: '/mois', popular: true, founder: true },
              { name: 'Premium', oldPrice: '150€', price: '75€', desc: '/mois', founder: true },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: item.popular ? 'linear-gradient(180deg, #FFF8F4 0%, #FFEEE4 100%)' : '#fff',
                border: item.popular ? `2px solid ${C.orange}` : `1px solid ${C.gray200}`,
                borderRadius: 10,
                position: 'relative',
              }}>
                {item.popular && (
                  <div style={{
                    position: 'absolute',
                    top: -10,
                    right: 12,
                    background: C.orange,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 6,
                  }}>POPULAIRE</div>
                )}
                <div style={{ fontWeight: 600, fontSize: 14, color: C.navy }}>
                  Formule {item.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  {item.oldPrice && (
                    <span style={{ textDecoration: 'line-through', color: C.gray400, fontSize: 13 }}>{item.oldPrice}</span>
                  )}
                  <span style={{ fontWeight: 800, fontSize: 20, color: C.orange }}>{item.price}</span>
                  <span style={{ fontSize: 11, color: C.gray500 }}>{item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Header - Tarifs sans surprise */}
      <div style={{ textAlign: 'center', marginTop: 48, marginBottom: 24 }}>
        <span className="eyebrow eyebrow-orange" style={{ 
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12), rgba(234, 88, 12, 0.08))',
          border: '1px solid rgba(249, 115, 22, 0.25)',
          color: '#EA580C',
        }}>
          <span className="dot" style={{ background: '#F97316' }} />
          Tarif sans surprise
        </span>
        <h2 className="t-h2" style={{ marginTop: 20 }}>
          Choisis la formule <span className="accent">qui te ressemble.</span>
        </h2>
        <p className="t-lead" style={{ maxWidth: 600, margin: '16px auto 0' }}>
          Démarre gratuitement, lance ta boutique virtuelle. Puis déploie les supers pouvoirs de ton assistant, de tes agents IA selon ta formule.
        </p>
      </div>

      {/* CTA */}
      <div style={{ textAlign: 'center' }}>
        <a 
          href="#tarifs" 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '16px 32px',
            background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
            color: '#fff',
            borderRadius: 14,
            fontWeight: 700,
            fontSize: 16,
            textDecoration: 'none',
            boxShadow: '0 8px 28px rgba(249,115,22,0.4)',
            transition: 'all 0.25s ease',
          }}
          onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 12px 35px rgba(249,115,22,0.5)'; }}
          onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 8px 28px rgba(249,115,22,0.4)'; }}
        >
          Voir toutes les formules →
        </a>
      </div>

    </div>
  </section>
);

const SetupSection = () => (
  <section id="setup" className="section section-cream">
    <div className="container-narrow">
      <div className="section-head">
        <span className="eyebrow eyebrow-orange">Paramétrage clé en main</span>
        <h2 className="t-h2">Notre équipe configure<br /><span className="accent">tout pour toi.</span></h2>
        <p className="t-lead">En 7 jours, ton business est prêt. Notre équipe configure absolument tout.</p>
      </div>
      <div className="setup-grid">
        {[
          { t: 'Ta boutique virtuelle', d: 'Tes prestations, tes produits, tes informations, ton réseau social sont en ligne. Tu es prêt à vendre.' },
          { t: 'Ton site web', d: 'Ton propre nom de domaine. Optimisé pour un maximum de visibilité. Prestations, produits, informations, réseaux sociaux.', badge: 'Business ou Premium' },
          { t: 'Ton Super Assistant formé', d: 'On le forme à ton métier, tes prestations, ton fonctionnement. Opérationnel dès le premier jour.', badge: 'Pro, Business ou Premium' },
          { t: 'Ton Vendeur IA formé', d: 'Chaque pro a un vendeur IA dans sa boutique. On le forme à tes produits pour qu\'il vende à ta place.', badge: 'Pro, Business ou Premium' },
          { t: 'Tes 4 agents IA', d: 'Service Client, Commercial, Administratif, Community Manager. Formés à ton activité.', badge: 'Business et Premium' },
          { t: 'Tes 8 agents supplémentaires', d: 'Gestionnaire de Missions, Modérateur, Graphiste, Webmaster, Conseil Juridique, Analyste Financier, Monteur Vidéo, Marketing.', badge: 'Premium' },
          { t: 'Ton outil de gestion paramétré', d: 'Équipe, mails, liste de tâches, prospects, clients, devis, factures... Tu parles à ton assistant, il exécute.', badge: 'Pro, Business ou Premium' },
          { t: 'Ton catalogue organisé', d: 'Produits ou prestations importées et structurées : stock, prix, photos, tout en ordre dès le premier jour.', badge: 'Pro, Business ou Premium' },
          { t: 'Tes réseaux connectés', d: 'Facebook, Instagram, TikTok, LinkedIn, tout est relié. Tu postes, tu réponds aux commentaires et messages privés du même endroit. De ton cockpit.', badge: 'Pro, Business ou Premium' },
        ].map((s, i) => (
          <div key={i} className="setup-card">
            {s.badge && <span className="setup-badge">{s.badge}</span>}
            <div className="setup-num">{i + 1}</div>
            <div className="setup-content">
              <div className="setup-title">{s.t}</div>
              <div className="setup-desc">{s.d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CommentSection = () => (
  <>
    <section id="comment" className="section section-cream" style={{ paddingBottom: 48 }}>
      <div className="container-narrow">
        <div className="section-head">
          <span className="eyebrow eyebrow-green">Simple comme bonjour</span>
          <h2 className="t-h2">Comment <span className="accent">ça marche ?</span></h2>
          <p className="t-lead">3 étapes. On s'occupe de tout. Toi tu n'as rien à faire.</p>
        </div>
        <div className="comment-grid">
          <div className="comment-step">
            <div className="comment-circle" style={{ background: 'rgba(37,87,167,0.1)' }}>🎯<div className="comment-num">1</div></div>
            <div className="comment-title">Tu choisis ta formule</div>
            <div className="comment-desc">Sélectionne ton offre et règle le pas de porte — une seule fois.</div>
            <span className="comment-tag" style={{ background: 'rgba(37,87,167,0.1)', color: C.navyMid }}>⏱ 5 minutes</span>
          </div>
          <div className="comment-arrow"><div>→</div></div>
          <div className="comment-step">
            <div className="comment-circle" style={{ background: 'rgba(249,115,22,0.1)' }}>⚙️<div className="comment-num">2</div></div>
            <div className="comment-title">On s'occupe de tout</div>
            <div className="comment-desc">On paramètre ton Cockpit, on forme tes agents IA et on crée ta boutique.</div>
            <span className="comment-tag" style={{ background: 'rgba(249,115,22,0.1)', color: C.orangeDeep }}>🛠 Notre équipe bosse</span>
          </div>
          <div className="comment-arrow"><div>→</div></div>
          <div className="comment-step">
            <div className="comment-circle" style={{ background: 'rgba(34,197,94,0.1)' }}>🚀<div className="comment-num">3</div></div>
            <div className="comment-title">En 7 jours c'est livré</div>
            <div className="comment-desc">Ton Cockpit, tes agents IA et ta boutique sont prêts à l'emploi.</div>
            <span className="comment-tag" style={{ background: 'rgba(34,197,94,0.1)', color: C.greenDark }}>✅ Prêt à l'emploi</span>
          </div>
        </div>
      </div>
    </section>
    <div className="delivery-banner">⏱ Livraison clé en main en 7 jours</div>
  </>
);

const FondateurSection = () => (
  <section id="fondateur" className="fondateur">
    <div className="orb-3"></div>
    <div className="orb-4"></div>
    <div className="hero-grid-tex"></div>
    <div className="inner">
      <span className="badge-line"><span className="dot"></span>👑 Offre limitée aux 100 premiers</span>
      <h2 className="display">Rejoins les<br /><span className="accent">Membres Fondateurs</span></h2>
      <div className="pills">
        <span className="pill">⭐ 2 ans d'abonnement offerts</span>
        <span className="pill">⭐ Paramétrage complet inclus</span>
        <span className="pill">⭐ Badge Fondateur exclusif</span>
        <span className="pill">⭐ Support prioritaire à vie</span>
      </div>
      <p className="desc">On s'occupe de TOUT pour toi en 7 jours.<br />Ta boutique, ton site, tes agents IA, tes devis — tout est prêt.<br /><strong>Tu arrives, tu travailles. C'est tout.</strong></p>
      <div className="cta-line">
        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">👑 Devenir Membre Fondateur →</a>
      </div>
      <div className="note">🛡️ Satisfait ou remboursé pendant 30 jours</div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════════════
// PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function EspaceProPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="ab-root" style={{ background: '#FFFBF7' }}>
        <GlobalStyles />
        <UnifiedHeader />
        <EspaceProNav />
        <EspaceProMobileAnchors />
        <EspaceProSectionsNav />
        <EspaceProSectionsNavSpacer />

        <Hero />
        <EcosystemeSection />
        <BoutiqueHeroSection />
        <DualSectionsIntro />
        <PrestatairesSection />
        <BoutiqueSection />
        <EncaissementSection />
        <EncaissementSectionDesktop />
        <CockpitHeroSection />
        <CrmSection />
        <AgentsHeroSection />
        <AssistantSection />
        <AgentsSection />
        
        <SeoMobile />
        <SocialMobile />
        
        <CommentSection />
        
        {/* Transition block - Parce que ton temps est important */}
        <section className="section show-on-mobile-only" style={{ paddingTop: 40, paddingBottom: 20 }}>
          <div className="container">
            <div style={{
              background: 'linear-gradient(135deg, #FFF8F4 0%, #FFEEE4 100%)',
              border: `2px solid ${C.orange}`,
              borderRadius: 24,
              padding: '20px 24px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(249,115,22,0.15)',
            }}>
              <style>{`
                @keyframes floatBubble1 {
                  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
                  50% { transform: translate(-10px, -15px) scale(1.1); opacity: 0.5; }
                }
                @keyframes floatBubble2 {
                  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.2; }
                  50% { transform: translate(8px, 10px) scale(1.15); opacity: 0.4; }
                }
                @keyframes bounceArrow {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(6px); }
                }
              `}</style>
              <div style={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(249,115,22,0.15)',
                animation: 'floatBubble1 4s ease-in-out infinite',
              }} />
              <div style={{
                position: 'absolute',
                bottom: -15,
                left: -15,
                width: 50,
                height: 50,
                borderRadius: '50%',
                background: 'rgba(249,115,22,0.12)',
                animation: 'floatBubble2 3s ease-in-out infinite 0.5s',
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 10,
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'rgba(249,115,22,0.08)',
                animation: 'floatBubble1 5s ease-in-out infinite 1s',
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.5,
                  marginBottom: 12,
                }}>
                  <span style={{ color: C.navy }}>Parce que ton temps est important. </span>
                  <span style={{ color: C.orange }}>Et que tu mérites le meilleur outil.</span>
                </div>
                
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: C.orange,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'bounceArrow 2s ease-in-out infinite',
                  boxShadow: '0 4px 15px rgba(249,115,22,0.4)',
                  margin: '0 auto',
                }}>
                  <span style={{ color: '#fff', fontSize: 16 }}>↓</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <SetupSection />
        <PricingDesktop />
        <PricingMobile />
        
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
